<template>
  <!-- Need to add height inherit because Vue 2 don't support multiple root ele -->
  <div style="height: inherit">
    <b-overlay
      :show="loading || socketConnecting"
      no-center
      variant="transparent"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>
    </b-overlay>
    <div
      class="body-content-overlay"
      :class="{'show': shallShowUserProfileSidebar || shallShowActiveChatContactSidebar || mqShallShowLeftSidebar}"
      @click="mqShallShowLeftSidebar=shallShowActiveChatContactSidebar=shallShowUserProfileSidebar=false"
    />

    <portal to="message-information">
      <div class="message-information">
        <div class="remaining-message">
          <div class="chat-icon">
            <b-img
              :src="iconChat3"
              width="28"
              height="28"
            />
          </div>
          <div class="remaining-message-text">
            <div v-if="!messageCountLoading">
              <p v-if="freeChat">
                {{ $t('You have unlimited messages.') }}
              </p>
              <div v-else>
                <p v-if="messageTimeLeft <= 0 && messageCount <= 0">
                  {{ $t('You do not have the right to message, you can buy the package you want to enjoy the chat.') }}
                </p>
                <p
                    v-if="!messageCountLoading && messageTimeLeft > 0"
                    v-html="$t('You have unlimited messages. Enjoy messaging for next {time}', {time: messageTimeLeftForHuman})"
                />
                <div v-else>
                  <p
                      v-if="!messageCountLoading && messageCount > 0"
                      v-html="$t('You’ve {count} messages. Enjoy messaging.', {count: messageCount})"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="market-button">
          <router-link :to="{name: 'market'}">
            <b-img
              :src="iconDiamonds1"
              width="20"
              height="20"
            />
            {{ $t('Market') }}
          </router-link>
        </div>
      </div>
    </portal>

    <!-- Main Area -->
    <section class="chat-app-window">

      <!-- Start Chat Logo -->
      <div
        v-if="!loading && (!activeChat || !activeChat.contact)"
        class="start-chat-area"
      >
        <div class="mb-1 start-chat-icon">
          <feather-icon
            icon="MessageSquareIcon"
            size="56"
          />
        </div>
        <h4
          class="sidebar-toggle start-chat-text"
          @click="startConversation"
        >
          {{ $t('Start chat') }}
        </h4>
      </div>

      <!-- Chat Content -->
      <div
        v-else-if="!loading && activeChat && activeChat.contact"
        class="active-chat"
      >
        <!-- Ambient photo watermark background -->
        <div
          v-if="activeChat && activeChat.contact && activeChat.contact.profile && activeChat.contact.profile.image"
          class="chat-ambient-background"
          :style="{ backgroundImage: 'url(' + activeChat.contact.profile.image + ')' }"
        />

        <!-- Chat Navbar -->
        <div class="chat-navbar">
          <header class="chat-header">

            <!-- Avatar & Name -->
            <div class="d-flex align-items-center">

              <!-- Toggle Icon -->
              <div class="sidebar-toggle d-block d-lg-none mr-1">
                <feather-icon
                  icon="ArrowLeftIcon"
                  class="cursor-pointer text-white"
                  size="18"
                  @click="mqShallShowLeftSidebar = true"
                />
              </div>

              <b-avatar
                size="36"
                :src="activeChat.contact.profile.image"
                class="mr-1 cursor-pointer badge-minimal"
                badge
                :badge-variant="resolveAvatarBadgeVariant(activeChat.contact.profile.onlineStatus)"
                @click.native="openContactProfile"
              />
              <div
                class="cursor-pointer"
                @click="openContactProfile"
              >
                <h6 class="mb-0 chat-user-username text-white">
                  {{ activeChat.contact.userName }}
                </h6>
                <div class="chat-header-status-row">
                  <span
                    class="chat-online-dot"
                    :class="{ offline: activeChat.contact.profile.onlineStatus !== 'online' }"
                  />
                  <span
                    class="chat-online-text"
                    :class="{ offline: activeChat.contact.profile.onlineStatus !== 'online' }"
                  >{{ activeChat.contact.profile.onlineStatus === 'online' ? $t('Online') : $t('Offline') }}</span>
                </div>
              </div>
            </div>

            <!-- Contact Actions -->
            <div
              v-if="activeChat && activeChat.contact && !activeChat.contact.isAssistant"
              class="d-flex align-items-center"
            >
              <div
                v-if="activeChat.extended && activeChat.extended.profile.cameraOn"
                class="chat-header-icon-btn mr-1 cursor-pointer"
                @click="videoCall(activeChat.contact)"
                :title="$t('Video Call')"
              >
                <feather-icon icon="VideoIcon" size="16" />
              </div>
              <div
                v-if="activeChat.extended && activeChat.extended.profile.voiceOn"
                class="chat-header-icon-btn mr-1 cursor-pointer"
                @click="call(activeChat.contact)"
                :title="$t('Voice Call')"
              >
                <feather-icon icon="PhoneIcon" size="16" />
              </div>
              <div
                class="chat-header-icon-btn mr-1 cursor-pointer"
                @click="shallShowActiveChatContactSidebar = !shallShowActiveChatContactSidebar"
                :title="$t('Profile')"
              >
                <feather-icon icon="InfoIcon" size="16" />
              </div>
              <div class="dropdown">
                <b-dropdown
                  v-if="!activeChat.contact.isAssistant"
                  variant="link"
                  no-caret
                  toggle-class="p-0"
                  menu-class="chat-header-menu"
                  right
                >
                  <template #button-content>
                    <div class="chat-header-icon-btn">
                      <feather-icon icon="MoreVerticalIcon" size="16" />
                    </div>
                  </template>
                  <b-dropdown-item :to="{name: 'users.show', params: {id: activeChat.contact.id}}">
                    <feather-icon icon="UserIcon" size="15" class="chat-menu-icon" />
                    {{ $t('View profile') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="!activeChat.contact.blockedByUser"
                    @click="blockUser(activeChat.contact.id)"
                  >
                    <feather-icon icon="SlashIcon" size="15" class="chat-menu-icon" />
                    {{ $t('Block user') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-else
                    @click="unBlockUser(activeChat.contact.id)"
                  >
                    <feather-icon icon="CheckCircleIcon" size="15" class="chat-menu-icon" />
                    {{ $t('Unblock') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    link-class="chat-menu-report"
                    @click="modalReportUser = true"
                  >
                    <feather-icon icon="FlagIcon" size="15" class="chat-menu-icon" />
                    {{ $t('Report') }}
                  </b-dropdown-item>
                  <b-dropdown-item
                    link-class="chat-menu-danger"
                    @click="deleteChatHistory(activeChat.contact.id)"
                  >
                    <feather-icon icon="Trash2Icon" size="15" class="chat-menu-icon" />
                    {{ $t('Delete chat') }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </header>
        </div>

        <!-- User Chat Area -->
        <vue-perfect-scrollbar
          ref="refChatLogPS"
          :settings="perfectScrollbarSettings"
          class="user-chats scroll-area"
          @scroll.native="onChatLogScroll"
        >
          <!-- No Chat -->
          <div
            v-if="!loading && activeChat && ((Array.isArray(activeChat.chat) && activeChat.chat.length === 0) || !activeChat.chat)"
            class="start-chat-area start-chat-area--empty"
          >
            <!-- Boho temalı boş sohbet görseli (önceden gri daire içinde beyaz ikon vardı) -->
            <div class="mb-1 empty-chat-icon">
              <svg width="132" height="132" viewBox="0 0 132 132" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <defs>
                  <linearGradient id="bohoEmptyChatGradient" x1="16" y1="12" x2="116" y2="120" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stop-color="#EA03B2" />
                    <stop offset="1" stop-color="#7C3AED" />
                  </linearGradient>
                </defs>
                <circle cx="66" cy="66" r="62" fill="url(#bohoEmptyChatGradient)" opacity="0.18" />
                <circle cx="66" cy="66" r="48" fill="url(#bohoEmptyChatGradient)" />
                <path d="M45 52c0-6.1 4.9-11 11-11h20c6.1 0 11 4.9 11 11v12c0 6.1-4.9 11-11 11H62l-10 8v-8.4c-4.1-1.6-7-5.6-7-10.2V52z" fill="#fff" />
                <path d="M66 67.5l-1-.9c-3.6-3.3-6-5.4-6-8 0-2.1 1.6-3.7 3.7-3.7 1.2 0 2.3.5 3 1.4.7-.9 1.9-1.4 3-1.4 2.1 0 3.7 1.6 3.7 3.7 0 2.6-2.4 4.7-6 8l-1 .9z" fill="#EA03B2" />
                <path d="M100 30l1.6 3.6L105 35l-3.4 1.4L100 40l-1.6-3.6L95 35l3.4-1.4L100 30z" fill="#FFCF64" />
                <path d="M30 92l1.2 2.6 2.6 1.2-2.6 1.2L30 99.6l-1.2-2.6-2.6-1.2 2.6-1.2L30 92z" fill="#FFCF64" />
              </svg>
            </div>
            <h4 class="empty-chat-text">
              {{ $t('No messages yet') }}
            </h4>
            <p class="empty-chat-subtext">
              {{ $t('Say hello and start the conversation') }}
            </p>
          </div>

          <chat-log
            ref="refChatLog"
            :all-messages-loaded="allMessagesLoaded"
            :loading="loadingMore"
            :chat-data="activeChat"
            :profile-user-avatar="profileUserDataMinimal.profile.image"
            :is-typing="isContactTyping"
            @loadMore="loadMore"
            @image-loaded="onChatImageLoaded"
            @send-ai-feedback="handleAiFeedback"
            @open-guul-match="joinGuulMatch"
          />
        </vue-perfect-scrollbar>

        <div
          v-if="activeChat.contact"
          class="chat-assistant-tools"
        >
          <div
            v-if="showAssistantCallPrompt && activeChatCallAction"
            class="assistant-call-prompt"
          >
            <button
              class="assistant-call-prompt__close"
              @click="dismissAssistantCallPrompt"
            >
              <feather-icon icon="XIcon" size="18" />
            </button>
            <h3>{{ $t("Let's talk!") }}</h3>
            <p>{{ activeChatCallAction.description }}</p>
            <button
              class="assistant-call-prompt__cta"
              @click="startActiveChatCall"
            >
              <span>{{ activeChatCallAction.label }}</span>
              <feather-icon icon="ChevronRightIcon" size="22" />
            </button>
            <div class="assistant-call-prompt__secure">
              <feather-icon icon="ShieldIcon" size="16" />
              {{ $t('Private & secure calls') }}
            </div>
          </div>
        </div>

        <!-- Action Buttons: ASK FOR A PHOTO & ASK FOR A VIDEO (Left-aligned & Enlarged) -->
        <div
          v-if="activeChat && activeChat.contact && !isFreeCallUser"
          class="chat-floating-actions"
        >
          <button
            type="button"
            class="chat-floating-btn"
            :disabled="mediaRequestLoading"
            @click="requestAssistantMedia('photo')"
          >
            <feather-icon icon="CameraIcon" size="17" class="mr-50" />
            <span>{{ $t('ASK FOR A PHOTO') }}</span>
          </button>
          <button
            type="button"
            class="chat-floating-btn"
            :disabled="mediaRequestLoading"
            @click="requestAssistantMedia('video')"
          >
            <feather-icon icon="VideoIcon" size="17" class="mr-50" />
            <span>{{ $t('ASK FOR A VIDEO') }}</span>
          </button>
        </div>

        <!-- Message Input -->
        <div class="chat-footer-area">
          <b-form
            class="chat-app-form"
            @submit.prevent="socketConnected && chatInputMessage.trim() && (freeChat || activeChat.contact.isAssistant || messageCount > 0 || messageTimeLeft > 0) ? send() : null"
          >
            <div
              class="chat-modern-input-bar"
              @click="!freeChat && messageCount <= 0 && messageTimeLeft <= 0 && !activeChat.contact.isAssistant ? goMarket() : null"
            >
              <!-- disabled yerine readonly: bağlantı kısa süre koptuğunda klavye kapanmasın -->
              <b-form-input
                ref="refChatInput"
                v-model="chatInputMessage"
                class="chat-modern-input-field"
                :readonly="!socketConnected"
                :placeholder="messageInputText"
                enterkeyhint="send"
                autocomplete="off"
                @input="onTyping"
              />

              <button
                type="submit"
                class="chat-modern-send-btn"
                :disabled="!socketConnected || !chatInputMessage.trim()"
                @mousedown.prevent
              >
                <feather-icon icon="SendIcon" size="16" />
              </button>
            </div>
          </b-form>
        </div>
      </div>
    </section>

    <!-- Active Chat Contact Details Sidebar -->
    <chat-active-chat-content-details-sidedbar
      :shall-show-active-chat-contact-sidebar.sync="shallShowActiveChatContactSidebar"
      :contact="activeChat.extended || {}"
    />

    <!-- Sidebar -->
    <portal to="content-renderer-sidebar-left">
      <chat-left-sidebar
        :chats-contacts="chatsContacts"
        :community-contacts="communityContacts"
        :current-mood="currentMood"
        :mood-options="moodOptions"
        :chat-sort="chatSort"
        :chat-mood-id="chatMoodId"
        :active-chat-contact-id="activeChat.contact ? activeChat.contact.id : null"
        :shall-show-user-profile-sidebar.sync="shallShowUserProfileSidebar"
        :profile-user-data="profileUserData"
        :profile-user-minimal-data="profileUserDataMinimal"
        :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
        @show-user-profile="showUserProfileSidebar"
        @open-chat="openChatOfContact"
        @open-mood-modal="moodModal = true"
        @set-sort-most-recent="setChatSortMostRecent"
        @set-mood-filter="setChatMoodFilter"
      />
    </portal>

    <!-- Mood Selection Modal -->
    <b-modal
      v-model="moodModal"
      centered
      hide-header
      hide-footer
      modal-class="mood-modal"
      content-class="mood-modal-content"
    >
      <div class="mood-modal-body">
        <div class="mood-modal-header">
          <span class="mood-title">{{ $t('Set status...') }}</span>
          <button class="mood-edit-btn" @click="moodModal = false">
            <feather-icon icon="EditIcon" size="14" />
            {{ $t('Edit') }}
          </button>
        </div>
        <div class="mood-options">
          <div
            v-for="mood in moodOptions"
            :key="mood.id"
            class="mood-option"
            :class="{ 'selected': selectedMoodId === mood.id }"
            @click="selectedMoodId = mood.id"
          >
            {{ mood.name }} {{ mood.emoji }}
          </div>
        </div>
        <div class="mood-actions">
          <button class="mood-cancel-btn" @click="moodModal = false">
            {{ $t('Cancel') }}
          </button>
          <button class="mood-save-btn" @click="saveMood">
            {{ $t('Save') }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Hediye satışı kaldırıldı — hediye gönderme modalı devre dışı
<b-modal
      v-if="selectedGift"
      v-model="giftModal"
      centered
      hide-header-close
      hide-footer
      size="sm"
      :title="activeChat ? activeChat.contact.userName + ' için hediye' : ''"
    >
      <div class="gifts-modal">
        <h4>{{ selectedGift.label }}</h4>
        <b-img
          :src="getImgGift(selectedGift.image)"
          width="128"
          :alt="selectedGift.label"
        />
        <h4 v-if="userGender === 'male'">
          {{ selectedGift.credits }} Elmas
        </h4>
        <b-button
          variant="primary"
          block
          :disabled="loading || socketConnecting"
          @click="sendGift(selectedGift)"
        >
          {{ $t('Send') }}
        </b-button>
        <b-button
          variant="outline-dark"
          size="sm"
          :disabled="loading || socketConnecting"
          @click="hideGiftModal(true)"
        >
          {{ $t('Choose Another Gift') }}
        </b-button>
      </div>
    </b-modal>
    -->

    <b-modal
      v-if="activeChat.contact"
      v-model="modalReportUser"
      :title="$t('Report {name}', { name: activeChat.contact.userName })"
      modal-class="chat-report-modal"
      centered
      hide-header-close
      hide-footer
    >
      <div>
        <validation-observer
          v-if="reportReasonTypes.length"
          ref="reportForm"
          #default="{}"
        >
          <b-form
            class="mt-2"
            @submit.prevent
          >

            <b-form-group
              label-for="reason"
              :label="$t('Report reason')"
            >
              <validation-provider
                #default="{ errors }"
                :name="$t('Report reason')"
                vid="reason"
                rules="required"
              >
                <v-select
                  v-model="report.reason"
                  :clearable="false"
                  :disabled="loading"
                  label="title"
                  :options="reportReasonTypes"
                  :reduce="option => option.value"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>

              <b-form-group
                v-if="report.reason === 'Other'"
                label-for="reportMessage"
                :label="$t('Your message')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Your message')"
                  vid="description"
                  rules="required"
                >
                  <b-form-textarea
                    id="description"
                    v-model="report.description"
                    :disabled="loading"
                    :placeholder="$t('Your message')"
                    rows="3"
                    max-rows="6"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-form-group>

            <b-button
              variant="primary"
              class="mt-2 px-4 chat-report-submit"
              :disabled="loading || !report.reason"
              @click="reportUser"
            >
              <b-spinner
                v-if="loading"
                small
              />
              {{ $t('Report') }}
            </b-button>
          </b-form>
        </validation-observer>
      </div>
    </b-modal>

    <!-- Hediye satışı kaldırıldı — hediye seçme modalı devre dışı
<b-modal
      v-model="chooseGiftModal"
      body-class="choose-gift-modal-body"
      scrollable
      hide-footer
      size="lg"
    >
      <div class="choose-gift-modal">
        <div
          v-for="gift in gifts"
          :key="gift.productId"
          class="gift"
        >
          <b-img
            width="64"
            :alt="gift.label"
            :src="getImgGift(gift.image)"
            class="gift-image"
            @click="showGiftModal(gift)"
          />
          <p v-if="userGender === 'male'">
            {{ numberFormat(gift.credits) }}
            <b-img
              :src="iconDiamonds1"
              width="16"
              height="16"
              style="margin-top: -2px"
            />
          </p>
        </div>
      </div>
    </b-modal>
    -->

    <b-modal
      v-model="premiumChatModal"
      centered
      hide-header
      hide-footer
      modal-class="premium-chat-modal"
      content-class="premium-chat-modal-content"
    >
      <div class="premium-chat-paywall">
        <button
          class="premium-chat-paywall__close"
          @click="premiumChatModal = false"
        >
          <feather-icon icon="XIcon" size="24" />
        </button>

        <div class="premium-chat-paywall__hero">
          <feather-icon icon="MessageSquareIcon" size="34" />
          <span>!</span>
        </div>

        <h2>{{ $t('Premium Access Required for Chat') }}</h2>
        <p>{{ $t('You need an active Premium Access package to continue this chat. Get Premium Access to enjoy:') }}</p>

        <div class="premium-chat-paywall__benefit">
          <div class="premium-chat-paywall__benefit-icon">
            <feather-icon icon="MessageCircleIcon" size="26" />
          </div>
          <div>
            <strong>{{ $t('Unlimited Chat') }}</strong>
            <span>{{ $t('without limits') }}</span>
          </div>
          <feather-icon
            icon="ZapIcon"
            size="24"
            class="premium-chat-paywall__spark"
          />
        </div>

        <button
          class="premium-chat-paywall__cta"
          @click="openPremiumPackage"
        >
          <span class="premium-chat-paywall__cta-icon">
            <feather-icon icon="AwardIcon" size="28" />
          </span>
          <span>
            <strong>{{ $t('Get Premium Access') }}</strong>
            <small>{{ $t('Unlock unlimited chat without limits') }}</small>
          </span>
          <feather-icon icon="ChevronRightIcon" size="28" />
        </button>
      </div>
    </b-modal>

    <!-- Guul: elmasla devam / yetersiz bakiye -->
    <b-modal
      v-model="guulPaywallModal"
      centered
      hide-header
      hide-footer
      modal-class="guul-paywall-modal"
      content-class="guul-paywall-modal-content"
    >
      <div class="guul-paywall">
        <button
          class="guul-paywall__close"
          :disabled="guulLoading"
          @click="closeGuulPaywall"
        >
          <feather-icon icon="XIcon" size="24" />
        </button>

        <div class="guul-paywall__hero">
          <feather-icon icon="CreditCardIcon" size="34" />
          <span><feather-icon icon="ZapIcon" size="22" /></span>
        </div>

        <h2>{{ $t('Use Diamonds to continue this premium feature') }}</h2>
        <p>{{ $t("You don't have Premium Access, but you can still use this feature with Diamonds.") }}</p>

        <div class="guul-paywall__row">
          <div class="guul-paywall__media-icon">
            <font-awesome-icon
              icon="fa-solid fa-dice"
              style="font-size: 24px;"
            />
          </div>
          <div class="guul-paywall__row-main">
            <span>{{ $t('This request requires') }}</span>
            <strong>{{ $t('{count} Diamonds', { count: numberFormat(guulRequiredCredits) }) }}</strong>
            <em class="guul-paywall__row-meta">
              {{ $t('Truth or Dare') }} · {{ $t('1 game') }}
            </em>
          </div>
        </div>

        <div class="guul-paywall__balance">
          <b-img
            :src="iconDiamonds1"
            width="48"
            height="48"
          />
          <div>
            <span>{{ $t('Your Diamonds') }}</span>
            <strong>{{ numberFormat(guulCurrentCredits) }}</strong>
          </div>
        </div>

        <button
          class="guul-paywall__cta"
          :disabled="guulLoading"
          @click="confirmGuulPaywall"
        >
          <b-img
            :src="iconDiamonds1"
            width="42"
            height="42"
          />
          <span>
            <strong>{{ guulInsufficientCredits ? $t('Buy Diamonds') : $t('Unlock Now') }}</strong>
            <small>{{ guulInsufficientCredits ? $t('Not enough diamonds') : $t('Continue with {count} Diamonds', { count: numberFormat(guulRequiredCredits) }) }}</small>
            <em>{{ guulInsufficientCredits ? $t('Add diamonds to continue') : $t('Get your Truth or Dare instantly!') }}</em>
          </span>
          <b-spinner
            v-if="guulLoading"
            small
          />
          <feather-icon
            v-else
            icon="ChevronRightIcon"
            size="28"
          />
        </button>

        <button
          v-if="guulPremiumPlacement"
          class="guul-paywall__secondary"
          :disabled="guulLoading"
          @click="openGuulPremiumPackage"
        >
          <feather-icon icon="AwardIcon" size="18" />
          {{ $t('Get Premium Access') }}
        </button>

        <div class="guul-paywall__note">
          <feather-icon icon="InfoIcon" size="16" />
          {{ $t('Without Premium Access, all premium features are pay-as-you-go.') }}
        </div>
      </div>
    </b-modal>

    <!-- Guul: oyun penceresi.
         Bilerek b-modal kullanılmıyor: Bootstrap modalı `.modal-open .modal { overflow-y: auto }`
         ile kaydırılabilir bir kapsayıcı yaratıyor ve iOS Safari bu kapsayıcı içindeki
         iframe'lerin dokunma koordinatlarını bozuyor. Sade bir position: fixed katman
         kullanılıyor ve açılırken doğrudan document.body'ye taşınıyor. -->
    <div
      v-if="guulGameOpen"
      ref="guulGameOverlay"
      class="guul-game-overlay"
    >
      <div class="guul-game-overlay__window">
        <button
          class="guul-game-overlay__close"
          @click="closeGuulGame"
        >
          <feather-icon icon="XIcon" size="24" />
        </button>
        <iframe
          v-if="guulMatchUrl"
          :src="guulMatchUrl"
          class="guul-game-overlay__frame"
          allow="camera; microphone; autoplay; fullscreen; clipboard-write"
          allowfullscreen
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import store from '@/store'
import { useRouter } from '@core/utils/utils'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  ref, onUnmounted, onMounted, nextTick, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import {
  BAvatar, BDropdown, BDropdownItem, BForm, BFormGroup, BFormInput, BFormTextarea, BInputGroup, BButton, BOverlay,
  BInputGroupText, BImg, BSpinner,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
// import { formatDate } from '@core/utils/filter'
import { $themeBreakpoints } from '@themeConfig'
import { useResponsiveAppLeftSidebarVisibility } from '@core/comp-functions/ui/app'
import useJwt from '@/auth/jwt/useJwt'
import _ from 'lodash'
import {
  gtmTrackNewEvent, showNotify, throwDefaultError, numberFormat,
} from '@/helpers/helpers'
import vSelect from 'vue-select'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import ChatLeftSidebar from './ChatLeftSidebar.vue'
import chatStoreModule from './chatStoreModule'
import ChatActiveChatContentDetailsSidedbar from './ChatActiveChatContentDetailsSidedbar.vue'
import ChatLog from './ChatLog.vue'
import useChat from './useChat'
import { logEvent } from 'firebase/analytics'

export default {
  name: 'Chat',
  components: {

    // BSV
    BAvatar,
    BDropdown,
    BDropdownItem,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BInputGroup,
    BButton,
    BOverlay,
    BInputGroupText,
    BImg,
    BSpinner,

    // 3rd Party
    VuePerfectScrollbar,

    // SFC
    ChatLeftSidebar,
    ChatActiveChatContentDetailsSidedbar,
    ChatLog,

    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  methods: {
    // Başlıktaki avatar/isme tıklayınca sağ panel yerine doğrudan kişinin profil sayfasına git.
    // Asistanın profil sayfası olmadığı için onda eski davranış (sağ panel) korunuyor.
    openContactProfile() {
      const contact = this.activeChat && this.activeChat.contact
      if (!contact) return
      if (contact.isAssistant || !contact.id) {
        this.shallShowActiveChatContactSidebar = !this.shallShowActiveChatContactSidebar
        return
      }
      this.$router.push({ name: 'users.show', params: { id: contact.id } })
    },
  },
  setup() {
    // Use toast
    const toast = useToast()

    const vm = getCurrentInstance().proxy

    const { t } = useI18nUtils()

    const CHAT_APP_STORE_MODULE_NAME = 'app-chat'

    const perfectScrollbarSettings = {
      maxScrollbarLength: 150,
      // Mesaj listesinin ucuna gelince kaydırma tüm sayfaya geçmesin (mobilde sayfa kayıyordu)
      swipePropagation: false,
      wheelPropagation: false,
    }

    // eslint-disable-next-line global-require
    const iconDiamonds1 = require('@/assets/images/icons/diamonds-1.svg')
    // eslint-disable-next-line global-require
    const iconChat3 = require('@/assets/images/icons/chat-3.png')
    // eslint-disable-next-line global-require
    const iconPhone = require('@/assets/images/icons/call2.svg')
    // eslint-disable-next-line global-require
    const iconVideoCall = require('@/assets/images/icons/camera.svg')
    // eslint-disable-next-line global-require
    const iconMoreHorizontal = require('@/assets/images/icons/more-horizontal-black.svg')

    const allMessagesLoaded = ref(true)
    const chatMessagesOffset = ref(0)
    const chatMessagesLimit = ref(200)
    const isContactTyping = ref(false)
    const typingStopTimer = ref(null)
    const isTypingSent = ref(false)
    const chooseGiftModal = ref(false)
    const giftModal = ref(false)
    const gifts = ref([])
    const loading = ref(false)
    // Eski mesajlar yüklenirken sohbet ekranı kaldırılmasın diye ayrı bayrak
    // (loading=true .active-chat'i kaldırıyor, klavye kapanıyor ve kaydırma konumu kayboluyordu)
    const loadingMore = ref(false)
    const messageCount = ref(0)
    const messageTimeLeft = ref(0)
    const freeChat = ref(false)
    const messageTimeLeftInterval = ref(null)
    const messageCountLoading = ref(false)
    const selectedGift = ref(null)
    const showGifts = ref(false)
    const modalReportUser = ref(false)
    const premiumChatModal = ref(false)
    const canShowPremiumChatPopup = () => {
      const source = store.getters['auth/userSource']
      return source !== undefined && source !== null && Number(source) <= 0
    }
    const moreWaysOpen = ref(false)
    const mediaRequestLoading = ref(false)
    const showAssistantCallPrompt = ref(false)
    const dismissedAssistantCallPrompts = ref({})
    const assistantCallPromptTimer = ref(null)
    const report = ref({
      reason: '',
      description: '',
    })
    const reportForm = ref(null)

    // $router and $route
    const $router = useRouter().router
    const $route = useRouter().route

    // Kalan mesaj süresi
    const messageTimeLeftForHuman = computed(() => {
      const timeLeft = messageTimeLeft.value

      if (timeLeft <= 0) {
        return 0
      }

      let result = ''
      let totalSeconds = timeLeft / 1000

      const days = Math.floor(totalSeconds / (60 * 60 * 24))
      totalSeconds -= days * (60 * 60 * 24)

      const hours = Math.floor(totalSeconds / (60 * 60))
      totalSeconds -= hours * (60 * 60)

      const minutes = Math.floor(totalSeconds / (60))
      totalSeconds -= minutes * 60

      // eslint-disable-next-line no-unused-vars
      const seconds = Math.floor(totalSeconds % 60)

      if (days > 0) {
        result += `${days} ${t('days')}, `
      }

      if (hours > 0) {
        result += `${hours} ${t('hours')}, `
      }

      if (minutes > 0) {
        result += `${minutes} ${t('minutes')}, `
      }

      // if (seconds > 0) {
      //   result += seconds + ` ${t('seconds')}, `
      // }

      return _.trimEnd(result, ', ')
    })
    const reportReasonTypes = computed(() => store.getters['app/reportReasonTypes'])

    const chatsContacts = ref([])
    const communityContacts = ref([])
    const chatsContactsLoaded = ref(false)

    // Mood state
    const moodModal = ref(false)
    const currentMood = ref(null)
    const selectedMoodId = ref(null)
    const moodOptions = ref([])

    // Mood listesini API'den çek
    const fetchMoodOptions = () => {
      useJwt.getChatCommunityMoods()
        .then(response => {
          moodOptions.value = response.data.body || []
        })
        .catch(() => {})
    }

    // Kullanıcının mevcut mood'unu çek
    const fetchMyMood = () => {
      useJwt.getChatCommunityMyMood()
        .then(response => {
          currentMood.value = response.data.body || null
          if (currentMood.value) {
            selectedMoodId.value = currentMood.value.id
          }
        })
        .catch(() => {})
    }

    const saveMood = () => {
      if (selectedMoodId.value) {
        useJwt.updateChatCommunityMood(selectedMoodId.value)
          .then(() => {
            const mood = moodOptions.value.find(m => m.id === selectedMoodId.value)
            currentMood.value = mood
            moodModal.value = false
          })
          .catch(error => {
            throwDefaultError(toast, error)
          })
      } else {
        moodModal.value = false
      }
    }

    // Mood verilerini yükle
    fetchMoodOptions()
    fetchMyMood()

    const routeParamsUserId = $route.value?.params?.userId
    watch(chatsContactsLoaded, value => {
      // route'tan userId parametresi geldiyse user bilgilerini çek veya mevcut chat varsa onu aktif et
      if (value && $route && routeParamsUserId) {
        // eslint-disable-next-line no-underscore-dangle
        const _existContact = chatsContacts.value.find(c => c.id === routeParamsUserId)
        if (_existContact) {
          // eslint-disable-next-line no-use-before-define
          openChatOfContact(routeParamsUserId)
        } else {
          // eslint-disable-next-line no-use-before-define
          getUser(routeParamsUserId)
        }
      }
    })

    // eslint-disable-next-line no-use-before-define
    // Websocket işlemleri
    const socket = computed(() => store.getters['socket/chatConnection'])
    const socketConnecting = computed({
      get: () => store.getters['socket/connecting'],
      set: value => store.commit('socket/updateConnecting', value),
    })
    const socketConnected = computed({
      get: () => store.getters['socket/connected'],
      set: value => store.commit('socket/updateConnected', value),
    })

    // const openSocket = () => {
    //   socket = io(process.env.VUE_APP_CHAT_SOCKET_URL,
    //       {
    //         reconnection: true,
    //         query: {
    //           uuid: store.getters['auth/userId'],
    //           'x-bpsh-connect-api-key': process.env.VUE_APP_API_KEY,
    //           'x-bpsh-connect-token': store.getters['auth/token']
    //         },
    //         transports: ['websocket', 'polling', 'flashsocket']
    //       })
    // }
    // openSocket()

    // eslint-disable-next-line no-nested-ternary,no-use-before-define
    const messageInputText = computed(() => ((!freeChat && messageCount.value <= 0 && messageTimeLeft.value <= 0 && activeChat.value && !activeChat.value.contact.isAssistant)
      ? t('You do not have the right to message. Click to buy.')
      : (socketConnected.value ? t('Send message') : t('Connecting to the system. Please wait.'))))

    // Komponent unmount olduğunda socketi kapat
    // onBeforeUnmount(() => {
    //   if (socket.value) {
    //     console.log(' socket.disconnect()',  socket.value.disconnect())
    //   }
    // })

    if (socket.value) {
      socket.value.on('reconnect_attempt', () => {})

      socket.value.on('reconnect', () => {})

      // Mesaj geldi
      socket.value.on('receive', chatMsgDTO => {
        try {
          // Eski ve yeni şema uyumluluğu: fromId/toId vs from/to
          const from = chatMsgDTO.fromId || chatMsgDTO.from
          const to = chatMsgDTO.toId || chatMsgDTO.to

          // Karşıdan gelen mesajsa typing indicator'ı kapat
          if (from !== store.getters['auth/userId']) {
            isContactTyping.value = false
          }

          // CHAT_CALL için hem gelen hem giden mesajları aktif chate ekle
          const isCallMsg = chatMsgDTO.type === 'CHAT_CALL'
          const contactId = activeChat.value && activeChat.value.contact && activeChat.value.contact.id
          const belongsToActiveChat = contactId && (contactId === from || contactId === to)

          // Media-request mesajları REST üzerinden oluşturulur; socket `send` ile gitmediği
          // için optimistic ekleme yapılmaz. Bu yüzden "kendi mesajımı atla" kuralına
          // yalnızca bu mesajlar için istisna tanınır — aksi halde gönderen tarafta hiç
          // görünmezler. Diğer tiplerde kural aynen korunmalı (yoksa çift görünürler).
          const isOwnMediaRequestMessage = chatMsgDTO.type === 'RICH_CONTENT_V1'
            && !!(chatMsgDTO.payload?.meta?.media_request || chatMsgDTO.payload?.meta?.mediaRequest)
            && from === store.getters['auth/userId']
            && belongsToActiveChat

          // Anlık olarak aktif chatte gösterilecek mi?
          // eslint-disable-next-line no-use-before-define
          if (activeChat.value && (activeChat.value.contact.id === from || isOwnMediaRequestMessage || (isCallMsg && belongsToActiveChat))) {
            // Gösterilecek mesaj içeriği; RICH_CONTENT_V1 için payload.body.text'e düş
            let messageContent = chatMsgDTO.content || chatMsgDTO.message || null
            if (!messageContent && chatMsgDTO.type === 'RICH_CONTENT_V1' && chatMsgDTO.payload?.body?.text) {
              messageContent = chatMsgDTO.payload.body.text
            }
            const mediaRequestBody = chatMsgDTO.body && typeof chatMsgDTO.body === 'object' ? chatMsgDTO.body : {}
            const mediaRequest = chatMsgDTO.payload?.meta?.media_request || chatMsgDTO.payload?.meta?.mediaRequest || {}

            const newMessage = {
              msgId: chatMsgDTO.msgId || null,
              fromId: from,
              toId: to,
              message: messageContent,
              translated_content: chatMsgDTO.translated_content || null,
              dateSent: chatMsgDTO.timestamp || new Date().getTime(),
              dateSeen: null,
              type: chatMsgDTO.type,
              gift: chatMsgDTO.gift || null,
              request_id: mediaRequest.requestId || mediaRequest.request_id || chatMsgDTO.request_id || mediaRequestBody.request_id || null,
              message_id: mediaRequest.messageId || mediaRequest.message_id || chatMsgDTO.message_id || mediaRequestBody.message_id || null,
              status: mediaRequest.status || chatMsgDTO.status || mediaRequestBody.status || null,
              content_type: mediaRequest.contentType || mediaRequest.content_type || chatMsgDTO.content_type || mediaRequestBody.content_type || null,
              blur_url: mediaRequest.blurUrl || mediaRequest.blur_url || chatMsgDTO.blur_url || mediaRequestBody.blur_url || null,
              normal_url: mediaRequest.normalUrl || mediaRequest.normal_url || chatMsgDTO.normal_url || mediaRequestBody.normal_url || null,
              thumbnail_url: mediaRequest.thumbnailUrl || mediaRequest.thumbnail_url || chatMsgDTO.thumbnail_url || mediaRequestBody.thumbnail_url || null,
              payload: chatMsgDTO.payload || null,
              callPayload: chatMsgDTO.callPayload || null,
            }

            // eslint-disable-next-line no-use-before-define
            activeChat.value.chat.push(newMessage)

            // Scroll'u aşağıya götür
            // RICH_CONTENT_V1 ile resim varsa @image-loaded event'i de scroll tetikler,
            // metin kısmı için hemen scroll yap
            nextTick(() => {
              nextTick(() => {
                // eslint-disable-next-line no-use-before-define
                scrollToBottomInChatLog()
              })
            })

            // Mesajları okundu olarak işaretle (kendi mesajım için anlamsız)
            if (!isOwnMediaRequestMessage) {
              useJwt.chatMarkSeen2(from)
                .then(() => {})
            }
          }
        } catch (err) {
          console.error(err)
        }

        // contact'ları yeniden çek
        // eslint-disable-next-line no-use-before-define
        fetchChatAndContacts(false)
      })

      // Mesaj gönderme başarısız
      socket.value.on('rejected', rspRejectedChatMsgDTO => {
        // rspRejectedChatMsgDTO:
        //     message: chatMsgDTO
        // reason: string
        //
        // reason listesi {
        //   "Cant find user",
        //       "Selected gift is not found",
        //       "Cant send gift to yourself",
        //       "Cant send gift to a blocked user",
        //       "Selected product is not a gift",
        //       "insufficient credits"
        // }

        // Aktif mesajlardan sil
        // eslint-disable-next-line no-use-before-define
        activeChat.value.chat = activeChat.value.chat.filter(m => !(m.message === rspRejectedChatMsgDTO.message.content && rspRejectedChatMsgDTO.message.type === 'CHAT_MSG'))

        // Aktif mesajlardan sil
        // activeChat.value.chat = activeChat.value.chat.filter(m => !(m.message === rspRejectedChatMsgDTO.message.content && rspRejectedChatMsgDTO.message.type === 'CHAT_MSG'))

        if (rspRejectedChatMsgDTO.reason_type === 'ErrNoCredit' && canShowPremiumChatPopup()) {
          premiumChatModal.value = true
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: rspRejectedChatMsgDTO.reason,
              variant: 'danger',
              hideClose: true,
            },
          })
        }
        giftModal.value = false

        // Chatcontacts'taki son mesajı sil
        // Tip mesaj ise son mesjaı değişkene ata
        if (rspRejectedChatMsgDTO.message.type === 'CHAT_MSG') {
          // eslint-disable-next-line no-use-before-define
          chatInputMessage.value = rspRejectedChatMsgDTO.message.content
        }

        // Chatcontacts'taki son mesajı sil

        loading.value = false

        // eslint-disable-next-line no-use-before-define
        nextTick(() => { scrollToBottomInChatLog() })
      })

      // Disconnect oldu
      socket.value.on('disconnect', reason => {
        socketConnected.value = false
        if (reason === 'io server disconnect') {
          // the disconnection was initiated by the server, you need to reconnect manually
          socket.value.connect()
        }
      })

      // Hediye gönderildi
      socket.value.on('giftSent', rspChatMsgDTO => {
        // giftDTO:
        //     createdAt: date (oluşturulma tarihi)
        // productId: string (ör: "com.arasana.gifts.v1.tea")
        // credits: integer (hediyenin kredisi)
        // targetUser: user (alıcı kişi bilgileri)
        // label: string (gönderen kişinin profil diline göre localized hediye ismi)
        // image: string (hediye imaj ismi)

        // eslint-disable-next-line no-use-before-define
        addMessageToChat({
          dateSent: new Date().getTime(),
          dateSeen: null,
          message: rspChatMsgDTO.gift.label,
          ...rspChatMsgDTO,
        })

        // eslint-disable-next-line no-use-before-define
        getMe()

        gtmTrackNewEvent('giftsent', {
          category: 'Chat',
          action: 'GiftSent',
          // eslint-disable-next-line no-use-before-define
          label: activeChat.value.contact.id,
        })

        loading.value = false
        showGifts.value = false
        selectedGift.value = null
      })

      // AI feedback onayı
      socket.value.on('ai_feedback_ack', ({ msg_id }) => {
        if (refChatLog.value) {
          refChatLog.value.dismissFeedback(msg_id)
        }
      })

      // Karşı taraf yazıyor
      socket.value.on('typingReceive', data => {
        if (activeChat.value && activeChat.value.contact && activeChat.value.contact.id === data.from) {
          isContactTyping.value = true
        }
      })

      // Karşı taraf yazmayı bıraktı
      socket.value.on('typingStopReceive', data => {
        if (activeChat.value && activeChat.value.contact && activeChat.value.contact.id === data.from) {
          isContactTyping.value = false
        }
      })

      // Hediye alındı
      socket.value.on('giftReceived', rspChatMsgDTO => {
        // rspChatMsgDTO:
        //     type: 'CHAT_GIFT'
        // content: productId
        // from: sender uuid
        // to: receiver uuid
        // gift: giftDTO (özellikle ui güncellemek üzere backward compatible gönderilen hediye bilgisi)

        // Aktif chat'e geldiyse ona ekle
        try {
          // eslint-disable-next-line no-use-before-define
          if (activeChat.value && activeChat.value.contact.id === rspChatMsgDTO.from) {
            // eslint-disable-next-line no-use-before-define
            activeChat.value.chat.push({
              fromId: rspChatMsgDTO.from,
              toId: rspChatMsgDTO.to,
              message: rspChatMsgDTO.content,
              dateSent: new Date().getTime(),
              dateSeen: null,
              type: rspChatMsgDTO.type,
              gift: rspChatMsgDTO.gift,
            })

            // Mesajları okundu olarak işaretle
            useJwt.chatMarkSeen(rspChatMsgDTO.from)
              .then(() => {})
          }
          // eslint-disable-next-line no-empty
        } catch (err) {}

        // contact'ları yeniden çek
        // eslint-disable-next-line no-use-before-define
        fetchChatAndContacts(true)
      })
    }

    // Computeds
    const profileUserData = computed(() => store.getters['auth/user'])
    const profileUserDataMinimal = computed(() => store.getters['auth/user'])
    const userGender = computed(() => store.getters['auth/userGender'])

    const storeUsers = computed(() => store.getters['users/users'])
    watch(
      storeUsers, value => {
        [...chatsContacts.value, ...communityContacts.value].forEach(user => {
          const storeUser = value.find(u => u.uuid === user.id)
          if (storeUser) {
            if (storeUser.status === 'USER_ONLINE') {
              // eslint-disable-next-line no-param-reassign
              user.profile.onlineStatus = 'online'
            } else if (storeUser.status === 'USER_OFFLINE') {
              // eslint-disable-next-line no-param-reassign
              user.profile.onlineStatus = 'offline'
            } else if (storeUser.status === 'USER_BUSY') {
              // eslint-disable-next-line no-param-reassign
              user.profile.busyInCall = true
            } else if (storeUser.status === 'USER_AVAILABLE') {
              // eslint-disable-next-line no-param-reassign
              user.profile.busyInCall = false
            }
          }
        })
      },
      {
        immediate: true,
        deep: true,
      },
    )

    // Register module
    if (!store.hasModule(CHAT_APP_STORE_MODULE_NAME)) store.registerModule(CHAT_APP_STORE_MODULE_NAME, chatStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      clearAssistantCallPromptTimer()
      if (store.hasModule(CHAT_APP_STORE_MODULE_NAME)) store.unregisterModule(CHAT_APP_STORE_MODULE_NAME)
    })

    onMounted(() => {
      if (window.innerWidth <= 770) {
        // eslint-disable-next-line no-use-before-define
        startConversation()
      }
    })

    // Mobil klavye: sohbet ekranı tam ekran yüksekliğinde olduğu için klavye açılınca küçülmüyor,
    // tarayıcı yazma alanını göstermek için bütün sayfayı yukarı itiyordu (ekran "kayıyordu").
    // Klavye açıkken yüksekliği görünen alana (visualViewport) eşitle, sayfayı yerinde tut ve
    // son mesaja in. Klavye kapalıyken hiçbir şey değişmez, görünüm aynı kalır.
    let keyboardOpen = false
    const onVisualViewportChange = () => {
      const vv = window.visualViewport
      if (!vv) return
      const open = window.innerWidth <= 991 && window.innerHeight - vv.height > 150
      // Mobilde açık sohbet tam ekran katmandır ve her zaman görünen alana (visualViewport) oturur
      // eslint-disable-next-line no-use-before-define
      const mobileChatOpen = window.innerWidth <= 768 && !!activeChat.value?.contact?.id
      const rootStyle = document.documentElement.style
      if (open || mobileChatOpen) {
        // iOS klavye açılınca görünen alanı yukarı kaydırıyor (offsetTop); sohbet bu alanı takip edip
        // tam görünen kısma oturur, böylece sayfa "kaymış" görünmez
        rootStyle.setProperty('--chat-vh', `${Math.round(vv.height)}px`)
        rootStyle.setProperty('--chat-vv-top', `${Math.round(vv.offsetTop)}px`)
      } else {
        rootStyle.removeProperty('--chat-vh')
        rootStyle.removeProperty('--chat-vv-top')
      }
      document.body.classList.toggle('chat-kb-open', open)
      if (open && !keyboardOpen) {
        // eslint-disable-next-line no-use-before-define
        nextTick(() => doScrollBottom())
      }
      keyboardOpen = open
    }
    onMounted(() => {
      // Sohbet sayfasında sayfanın kendisi kaymaz, yalnızca listeler kayar (bkz. html.chat-page)
      document.documentElement.classList.add('chat-page')
      if (!window.visualViewport) return
      window.visualViewport.addEventListener('resize', onVisualViewportChange)
      window.visualViewport.addEventListener('scroll', onVisualViewportChange)
    })
    onUnmounted(() => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onVisualViewportChange)
        window.visualViewport.removeEventListener('scroll', onVisualViewportChange)
      }
      document.documentElement.style.removeProperty('--chat-vh')
      document.documentElement.style.removeProperty('--chat-vv-top')
      document.body.classList.remove('chat-kb-open')
      document.documentElement.classList.remove('chat-page')
    })

    const { resolveAvatarBadgeVariant } = useChat()

    // Scroll to Bottom ChatLog
    const refChatLogPS = ref(null)
    const refChatInput = ref(null)
    const refChatLog = ref(null)

    const handleAiFeedback = ({ msg_id, value, ai_user_id }) => {
      if (!socket.value) return
      socket.value.emit('ai_feedback', { msg_id, value, ai_user_id })
    }

    const doScrollBottom = () => {
      if (!refChatLogPS.value) return
      const ps = refChatLogPS.value
      const el = ps.$el || ps
      if (ps.ps) ps.ps.update()
      el.scrollTop = el.scrollHeight
      // Tarayıcı layout'u tam yerleştirsin, sonra tekrar dene
      requestAnimationFrame(() => {
        if (ps.ps) ps.ps.update()
        el.scrollTop = el.scrollHeight
      })
    }

    // Mesaj listesi en alta yakın mı (kullanıcı yukarıda eski mesajları okuyorsa aşağı atlatma)
    const isChatNearBottom = () => {
      if (!refChatLogPS.value) return true
      const el = refChatLogPS.value.$el || refChatLogPS.value
      return el.scrollHeight - el.scrollTop - el.clientHeight < 200
    }

    const onChatImageLoaded = () => {
      if (loadingMore.value || !isChatNearBottom()) return
      // eslint-disable-next-line no-use-before-define
      scrollToBottomInChatLog()
    }

    const scrollToBottomInChatLog = () => {
      nextTick(() => {
        doScrollBottom()
        if (!refChatLogPS.value) return
        const ps = refChatLogPS.value
        const el = ps.$el || ps
        // Tüm görünen resimleri tara, her biri yüklenince scroll'u en alta çek
        const imgs = el.querySelectorAll('img')
        imgs.forEach(img => {
          if (img.complete && img.naturalHeight !== 0) return
          const onDone = () => {
            img.removeEventListener('load', onDone)
            img.removeEventListener('error', onDone)
            requestAnimationFrame(() => {
              requestAnimationFrame(doScrollBottom)
            })
          }
          img.addEventListener('load', onDone)
          img.addEventListener('error', onDone)
        })
      })
    }

    // Mesaj gönderme ortak fonksiyonu (normal mesaj ve hediye için)
    const sendMessage = (payload, addMessages = true, gift = null) => {
      // socket.value.emit('send', JSON.stringify(payload))
      socket.value.emit('send', payload)

      if (addMessages) {
        // eslint-disable-next-line no-use-before-define
        addMessageToChat({
          fromId: payload.from,
          toId: payload.to,
          message: payload.content,
          dateSent: new Date().getTime(),
          dateSeen: null,
          type: payload.type,
          gift,
        })
      }
    }
    const addMessageToChat = message => {
      // Aktif chat'e ekle
      // eslint-disable-next-line no-use-before-define
      activeChat.value.chat.push(message)

      // Contact'ları yeniden çek
      // eslint-disable-next-line no-use-before-define
      fetchChatAndContacts(false)

      // eslint-disable-next-line no-use-before-define
      chatInputMessage.value = ''

      // Kredi miktarını yeniden çek
      setTimeout(() => {
        // eslint-disable-next-line no-use-before-define
        getMe()
      }, 11)

      // Scroll'u aşağıya götür - DOM güncellemesini bekle
      nextTick(() => {
        nextTick(() => {
          scrollToBottomInChatLog()
        })
      })
    }

    // ------------------------------------------------
    // Chats & Contacts
    // ------------------------------------------------
    // 1v1 chat list sorting / mood filter (single-select segment)
    // chatSort: 'most_recent' | null ; chatMoodId: mood id | null (mutually exclusive)
    // Both null on first load => no params sent (backend default order, nothing selected)
    const chatSort = ref(null)
    const chatMoodId = ref(null)
    const syncUnreadChatCount = contacts => {
      const total = (contacts || []).reduce((sum, contact) => sum + (Number(contact.unSeenMsgCount) || 0), 0)
      store.dispatch('socket/setUnreadChatCount', total)
    }

    const fetchChatAndContacts = (showLoading = false) => {
      if (showLoading) {
        loading.value = true
      }
      useJwt.getChatUsers2(0, 100, { sort: chatSort.value, moodId: chatMoodId.value })
        .then(response => {
          chatsContacts.value = response.data.body || []
          chatsContactsLoaded.value = true
          syncUnreadChatCount(chatsContacts.value)

          // Scroll işlemini kaldırın - gereksiz
          // nextTick(() => { scrollToBottomInChatLog() })
        })
        .catch(() => {})
        .finally(() => {
          loading.value = false
        })
    }
    fetchChatAndContacts(true)

    // 1v1 list filter handlers (single-select: most_recent OR a mood)
    const setChatSortMostRecent = () => {
      // Toggle: re-clicking the active "Most Recent" clears it (back to default)
      chatSort.value = chatSort.value === 'most_recent' ? null : 'most_recent'
      chatMoodId.value = null
      fetchChatAndContacts(true)
    }
    const setChatMoodFilter = moodId => {
      chatMoodId.value = moodId
      chatSort.value = null
      fetchChatAndContacts(true)
    }

    // Community contacts'ı API'den çek
    const fetchCommunityContacts = () => {
      useJwt.getChatCommunityUsers(0, 100)
        .then(response => {
          const users = response.data.body || []
          communityContacts.value = users.map(user => ({
            id: user.uuid,
            userName: user.user_name,
            profile: {
              image: user.profile_photo_url,
              onlineStatus: user.is_connected ? 'online' : 'offline',
            },
            status: user.mood ? `${user.mood.name} ${user.mood.emoji}` : null,
            tag: user.tag,
            isFavorite: user.is_favorite,
            gender: user.gender,
            country: user.country,
          }))
        })
        .catch(() => {})
    }
    fetchCommunityContacts()

    const getMe = (showLoading = false, showLoadingMessage = false) => {
      if (showLoading) {
        loading.value = true
      }
      if (showLoadingMessage) {
        messageCountLoading.value = true
      }
      useJwt.getMe()
        .then(response => {
          messageCount.value = response.data.body.chatAccount.msgCount
          messageTimeLeft.value = response.data.body.chatAccount.timeLeft
          freeChat.value = response.data.body.freeChat
          store.dispatch('auth/updateUser', response.data.body)

          if (messageTimeLeft.value > 0) {
            if (messageTimeLeftInterval.value) {
              clearInterval(messageTimeLeftInterval.value)
              messageTimeLeftInterval.value = null
            }
            messageTimeLeftInterval.value = setInterval(() => {
              messageTimeLeft.value -= 1000
            }, 1000)
          }
        })
        .catch(() => {})
        .finally(() => {
          loading.value = false
          messageCountLoading.value = false
        })
    }
    getMe(true, true)

    // Hediye satışı kaldırıldı — hediye listesi artık çekilmiyor
    // const getGifts = (showLoading = false) => {
    //   if (showLoading) {
    //     loading.value = true
    //   }
    //   useJwt.getGifts()
    //     .then(response => {
    //       gifts.value = response.data.body
    //     })
    //     .catch(() => {})
    //     .finally(() => {
    //       loading.value = false
    //     })
    // }
    const getGifts = () => {}
    // Hediye satışı kaldırıldı
    // getGifts()

    // ------------------------------------------------
    // Single Chat
    // ------------------------------------------------
    const activeChat = ref({})
    const chatInputMessage = ref('')
    // Ham chat-friends listesi (activeChat.contact) AI alanini (isAssistant/isAi) taşımayabiliyor;
    // /users/{id} cevabından gelen activeChat.extended.isAi güvenilir referans olarak eklendi.
    const isAiContact = contact => !!(
      (contact && (contact.isAssistant || contact.isAi || contact.is_ai))
      || activeChat.value?.extended?.isAi
      || activeChat.value?.extended?.is_ai
    )
    const getCallProfile = () => activeChat.value?.extended?.profile || activeChat.value?.contact?.profile || {}
    const activeChatCallAction = computed(() => {
      const contact = activeChat.value?.contact
      if (!contact) return null

      if (isAiContact(contact)) {
        return {
          type: 'ai_voice',
          label: t('Voice Call'),
          description: t('Start a voice call and talk to me anytime.'),
        }
      }

      const profile = getCallProfile()
      if (profile.cameraOn) {
        return {
          type: 'video',
          label: t('Video Call'),
          description: t('Start a video call and talk face to face.'),
        }
      }

      if (profile.voiceOn) {
        return {
          type: 'voice',
          label: t('Voice Call'),
          description: t('Start a voice call and talk anytime.'),
        }
      }

      return null
    })
    const clearAssistantCallPromptTimer = () => {
      if (assistantCallPromptTimer.value) {
        clearTimeout(assistantCallPromptTimer.value)
        assistantCallPromptTimer.value = null
      }
    }
    const dismissAssistantCallPrompt = () => {
      const contactId = activeChat.value?.contact?.id
      if (contactId) {
        dismissedAssistantCallPrompts.value = {
          ...dismissedAssistantCallPrompts.value,
          [contactId]: true,
        }
      }
      showAssistantCallPrompt.value = false
      clearAssistantCallPromptTimer()
    }
    const startActiveChatCall = () => {
      const contact = activeChat.value?.contact
      const action = activeChatCallAction.value
      if (!contact || !action) return

      dismissAssistantCallPrompt()

      if (action.type === 'ai_voice') {
        vm.$root.$emit('ai-voice-call:start', activeChat.value.extended || contact)
        return
      }

      if (action.type === 'video') {
        videoCall(contact)
        return
      }

      call(contact)
    }
    const openChatOfContact = userId => {
      if (activeChat.value && activeChat.value?.contact?.id === userId) {
        return
      }
      loading.value = true

      // Reset send message input value
      chatInputMessage.value = ''
      isContactTyping.value = false
      isTypingSent.value = false
      if (typingStopTimer.value) {
        clearTimeout(typingStopTimer.value)
        typingStopTimer.value = null
      }

      // eslint-disable-next-line no-use-before-define
      loadMessages(userId)

      // if SM device =>  Close Chat & Contacts left sidebar
      // eslint-disable-next-line no-use-before-define
      mqShallShowLeftSidebar.value = false
    }

    const loadMessages = (userId, clear = true) => {
      // Eski mesajlar başa eklenince okunan yer kaymasın: yüklemeden önceki yüksekliği sakla
      let scrollEl = null
      let prevScrollHeight = 0
      if (clear) {
        chatMessagesOffset.value = 0
        allMessagesLoaded.value = true
      } else {
        if (loadingMore.value) return
        loadingMore.value = true
        scrollEl = refChatLogPS.value ? (refChatLogPS.value.$el || refChatLogPS.value) : null
        prevScrollHeight = scrollEl ? scrollEl.scrollHeight : 0
      }

      useJwt.getChat2(userId, chatMessagesOffset.value, chatMessagesLimit.value)
        .then(response => {
          // Önce chatsContacts'ta ara, yoksa communityContacts'ta ara
          let contact = chatsContacts.value.find(c => c.id === userId)
          if (!contact) {
            contact = communityContacts.value.find(c => c.id === userId)
          }

          if (contact && contact.unSeenMsgCount !== undefined) {
            contact.unSeenMsgCount = 0
            syncUnreadChatCount(chatsContacts.value)
          }

          if (clear) {
            activeChat.value = {
              contact,
              chat: response.data.body.result,
            }
          } else if (activeChat.value) {
            activeChat.value.chat.unshift(...response.data.body.result)
          }

          // eslint-disable-next-line no-use-before-define
          if (contact && contact.id) {
            getUser(contact.id)
          }

          chatMessagesOffset.value = response.data.body.offset + response.data.body.limit

          if (chatMessagesOffset.value >= response.data.body.totalCount) {
            allMessagesLoaded.value = true
          } else {
            allMessagesLoaded.value = false
          }

          // Mesajları okundu olarak işaretle
          useJwt.chatMarkSeen2(userId)
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => {
          if (!clear) {
            loadingMore.value = false
            nextTick(() => {
              if (scrollEl) {
                scrollEl.scrollTop = scrollEl.scrollHeight - prevScrollHeight
                if (refChatLogPS.value && refChatLogPS.value.ps) refChatLogPS.value.ps.update()
              }
            })
            return
          }
          loading.value = false
          if (clear) {
            nextTick(() => {
              nextTick(() => {
                scrollToBottomInChatLog()
              })
            })
          }
        })
    }

    // Daha fazla mesaj yükle
    const loadMore = () => {
      if (activeChat.value) {
        loadMessages(activeChat.value.contact.id, false)
      }
    }

    // Yukarı kaydırıp listenin tepesine yaklaşınca eski mesajlar kendiliğinden yüklenir
    // ("Load more" butonu yedek olarak kalır). Yalnızca kullanıcı yukarı kaydırırken tetiklenir;
    // sohbet açılırken en alta inilmesi (aşağı yönlü kaydırma) yükleme başlatmaz.
    let lastChatScrollTop = 0
    const onChatLogScroll = event => {
      const el = event && event.target
      if (!el) return
      const scrollingUp = el.scrollTop < lastChatScrollTop
      lastChatScrollTop = el.scrollTop
      if (!scrollingUp || el.scrollTop > 150) return
      if (allMessagesLoaded.value || loadingMore.value || !activeChat.value?.contact) return
      loadMore()
    }

    watch(isContactTyping, value => {
      if (value) {
        nextTick(() => { nextTick(() => { scrollToBottomInChatLog() }) })
      }
    })

    // Mobilde bir sohbet açıkken alt menü (tab bar) gizlenir ve mesaj kutusu ekranın altına oturur
    // (WhatsApp/Instagram gibi); önceden kutu tab bar'ın üstünde, sayfa kesik gibi duruyordu.
    // Sohbet listesine dönünce tab bar geri gelir. Stiller: body.chat-conversation-open
    watch(() => activeChat.value?.contact?.id, contactId => {
      document.body.classList.toggle('chat-conversation-open', !!contactId)
      onVisualViewportChange()
    }, { immediate: true })
    onUnmounted(() => {
      document.body.classList.remove('chat-conversation-open')
    })

    watch(() => activeChat.value?.contact?.id, contactId => {
      clearAssistantCallPromptTimer()
      showAssistantCallPrompt.value = false
      moreWaysOpen.value = false

      if (!contactId || dismissedAssistantCallPrompts.value[contactId]) return

      assistantCallPromptTimer.value = setTimeout(() => {
        if (activeChat.value?.contact?.id === contactId && activeChatCallAction.value && !dismissedAssistantCallPrompts.value[contactId]) {
          showAssistantCallPrompt.value = true
          nextTick(() => {
            nextTick(() => {
              scrollToBottomInChatLog()
            })
          })
        }
      }, 3500)
    })

    // freeCallUser hesaplar (içerik üreticileri) medya isteği gönderen taraf değil;
    // "Etkileşmenin diğer yolları" paneli onlara gösterilmez
    const isFreeCallUser = computed(() => !!store.getters['auth/freeCallUser'])

    // ---------------------------------------------------------------- Guul oyunu
    // Oyun yalnızca freeCallUser hesaplarda açık. Normal kullanıcılarla test etmek
    // gerekirse bunu true yapmak yeterli — başka hiçbir yeri değiştirmeye gerek yok.
    const GUUL_ENABLED_FOR_NORMAL_USERS = false

    // Kontakt AI alanını isAssistant / isAi / is_ai olarak taşıyabildiği ve chat-friends
    // listesinde hiç taşımayabildiği için tespit isAiContact üzerinden yapılıyor.
    const isActiveChatAi = computed(() => isAiContact(activeChat.value?.contact))

    const canStartGame = computed(() => {
      // AI asistanlarla oyun oynanmıyor
      if (isActiveChatAi.value) return false
      return isFreeCallUser.value || GUUL_ENABLED_FOR_NORMAL_USERS
    })

    // AI asistan sohbetlerinde "Etkileşmenin diğer yolları" paneli hiç gösterilmiyor
    // (fotoğraf isteği, video isteği ve oyun dahil) — cinsiyet ayrımı yok.
    const showMoreWaysPanel = computed(() => {
      if (isActiveChatAi.value) return false
      return !isFreeCallUser.value || canStartGame.value
    })

    const guulLoading = ref(false)
    const guulPaywallModal = ref(false)
    const guulPaywallInfo = ref(null)

    const guulRequiredCredits = computed(() => Number(guulPaywallInfo.value?.requiredCredits) || 0)
    const guulCurrentCredits = computed(() => Number(guulPaywallInfo.value?.currentCredits) || 0)
    // 10002/10003: elmas yetersiz -> satın almaya yönlendir
    const guulInsufficientCredits = computed(() => !!guulPaywallInfo.value?.insufficientCredits)
    // 10003: premium paketi de öner (placement2); 10001'de premium yoksa placement gelir
    const guulPremiumPlacement = computed(() => {
      const info = guulPaywallInfo.value
      if (!info || info.hasPremium) return null
      return info.placement2 || (guulInsufficientCredits.value ? null : info.placement) || null
    })

    const guulGameOpen = ref(false)
    const guulMatchUrl = ref(null)

    // Prod API'si embeddedLoginUrl'i http:// ile dönebiliyor; sayfa https olduğu için
    // tarayıcı frame'i mixed-content olarak engelliyor. Şemayı yükseltiyoruz —
    // engellenmektense denemek her durumda daha iyi. Asıl düzeltme backend'in https dönmesi.
    const normalizeGuulUrl = url => {
      if (typeof url !== 'string' || !url) return null
      if (url.startsWith('http://')) return `https://${url.slice('http://'.length)}`
      return url
    }

    const openGuulGame = url => {
      const secureUrl = normalizeGuulUrl(url)
      if (!secureUrl) {
        throwDefaultError(toast, new Error(vm.$t('Game could not be started')))
        return
      }

      guulMatchUrl.value = secureUrl
      guulGameOpen.value = true
    }

    const closeGuulGame = () => {
      guulGameOpen.value = false
      // iframe'i söküp oyun oturumunu arka planda çalışır bırakma
      guulMatchUrl.value = null
    }

    // Oyun içinden gelen köprü mesajları.
    // Guul'un closeChat()'i iOS/Android'de native köprüye postMessage atıyor;
    // webde karşılığı window.parent.postMessage. Aynı payload'ı ({ key: 'game-closed' })
    // hem nesne hem JSON string olarak kabul ediyoruz — Android tarafı stringify ediyor.
    const handleGuulMessage = event => {
      let expectedOrigin = null
      try {
        expectedOrigin = new URL(guulMatchUrl.value).origin
      } catch (e) {
        return
      }

      // Mesajı yalnızca oyunun kendi origin'inden kabul et
      if (!expectedOrigin || event.origin !== expectedOrigin) return

      let data = event.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch (e) {
          return
        }
      }

      if (data && data.key === 'game-closed') closeGuulGame()
    }

    // Katmanı body'ye taşı: chat sayfasındaki hiçbir kaydırma kapsayıcısı ya da
    // transform'lu ata elemanın altında kalmasın — iOS Safari böyle bir atanın
    // altındaki iframe'in dokunma koordinatlarını kaydırıyor.
    const detachGuulOverlay = () => {
      const el = vm.$refs.guulGameOverlay
      if (el && el.parentNode) el.parentNode.removeChild(el)
      document.body.classList.remove('guul-game-open')
    }

    watch(guulGameOpen, async open => {
      if (!open) {
        window.removeEventListener('message', handleGuulMessage)
        document.body.classList.remove('guul-game-open')
        return
      }

      await nextTick()
      const el = vm.$refs.guulGameOverlay
      if (el && el.parentNode !== document.body) document.body.appendChild(el)
      document.body.classList.add('guul-game-open')
      window.addEventListener('message', handleGuulMessage)
    })

    onUnmounted(() => {
      window.removeEventListener('message', handleGuulMessage)
      if (guulGameOpen.value) detachGuulOverlay()
      else document.body.classList.remove('guul-game-open')
    })

    const closeGuulPaywall = () => {
      if (guulLoading.value) return
      guulPaywallModal.value = false
      guulPaywallInfo.value = null
    }

    // Doküman: HTTP 200 olsa bile header.error doluysa success sayılmaz.
    // Bu yüzden başarı ve hata yolları tek yerde çözümleniyor.
    const resolveGuulResponse = (data, opponentUserId, err = null) => {
      const error = data?.header?.error
      const body = data?.body || {}

      if (!error) {
        if (body.embeddedLoginUrl) {
          guulPaywallModal.value = false
          guulPaywallInfo.value = null
          openGuulGame(body.embeddedLoginUrl)
          return
        }
        throwDefaultError(toast, err || new Error(vm.$t('Game could not be started')))
        return
      }

      const code = String(error.errorCode || '')

      // 10001 -> günlük hak bitti ama elmas yeterli: onay iste
      // 10002/10003 -> elmas yetersiz: satın alma akışı
      if (code === '10001' || code === '10002' || code === '10003') {
        guulPaywallInfo.value = {
          ...body,
          opponentUserId,
          insufficientCredits: code !== '10001',
        }
        guulPaywallModal.value = true
        return
      }

      throwDefaultError(toast, err || new Error(error.message))
    }

    const runGuulMatch = (opponentUserId, options = {}) => {
      if (!opponentUserId || guulLoading.value) return

      guulLoading.value = true
      useJwt.guulMatch(opponentUserId, options)
        .then(response => resolveGuulResponse(response?.data, opponentUserId))
        .catch(err => resolveGuulResponse(err?.response?.data, opponentUserId, err))
        .finally(() => {
          guulLoading.value = false
        })
    }

    // "Etkileşmenin diğer yolları" panelinden oyun başlatma
    const startGuulMatch = () => {
      const opponentUserId = activeChat.value?.contact?.id
      if (!opponentUserId) return

      moreWaysOpen.value = false
      runGuulMatch(opponentUserId, { isUsedCredits: false })
    }

    // Elmasla devam onayı / yetersiz bakiyede satın almaya yönlendirme
    const confirmGuulPaywall = () => {
      const info = guulPaywallInfo.value
      if (!info) return

      if (guulInsufficientCredits.value) {
        guulPaywallModal.value = false
        guulPaywallInfo.value = null
        // eslint-disable-next-line no-use-before-define
        $router.push({ name: 'market', params: { purchaseSource: 'premium_game' } })
        return
      }

      runGuulMatch(info.opponentUserId, { isUsedCredits: true })
    }

    const openGuulPremiumPackage = () => {
      guulPaywallModal.value = false
      guulPaywallInfo.value = null
      // eslint-disable-next-line no-use-before-define
      openPremiumPackage()
    }

    // Chat mesajındaki "Play" butonundan oyuna katılma.
    // Doküman: is_join true gönderilir, hak/kredi tüketilmez.
    const joinGuulMatch = payload => {
      const opponentUserId = payload?.opponentUserId
      if (!opponentUserId) {
        throwDefaultError(toast, new Error(vm.$t('Game could not be started')))
        return
      }

      runGuulMatch(opponentUserId, { isJoin: true })
    }

    const requestAssistantMedia = mediaType => {
      const contactId = activeChat.value?.contact?.id
      if (!contactId || mediaRequestLoading.value) return

      mediaRequestLoading.value = true
      useJwt.chatMediaRequest(contactId, mediaType)
        .then(() => {
          moreWaysOpen.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: vm.$t('Request sent. They will see it in the chat.'),
              icon: mediaType === 'video' ? 'VideoIcon' : 'CameraIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => {
          mediaRequestLoading.value = false
        })
    }

    const onTyping = () => {
      if (!socket.value || !activeChat.value?.contact) return
      const payload = { from: profileUserDataMinimal.value.id, to: activeChat.value.contact.id }

      if (!isTypingSent.value) {
        socket.value.emit('typingSend', payload)
        isTypingSent.value = true
      }

      if (typingStopTimer.value) clearTimeout(typingStopTimer.value)
      typingStopTimer.value = setTimeout(() => {
        socket.value.emit('typingStopSend', payload)
        isTypingSent.value = false
      }, 3000)
    }

    const send = () => {
      if (!chatInputMessage.value.trim()) return

      // Gönderince klavye kapanmasın: odak aynı dokunuş içinde yazma alanına geri verilir
      if (refChatInput.value && refChatInput.value.focus) refChatInput.value.focus()

      const payload = {
        from: profileUserDataMinimal.value.id,
        to: activeChat.value.contact.id,
        type: 'CHAT_MSG',
        content: chatInputMessage.value.trim(),
      }

      // Typing state'i sıfırla
      if (typingStopTimer.value) {
        clearTimeout(typingStopTimer.value)
        typingStopTimer.value = null
      }
      if (isTypingSent.value && socket.value) {
        socket.value.emit('typingStopSend', { from: profileUserDataMinimal.value.id, to: activeChat.value.contact.id })
      }
      isTypingSent.value = false

      gtmTrackNewEvent('chatsent', {
        category: 'Chat',
        action: 'ChatSent',
        label: activeChat.value.contact.id,
      })

      sendMessage(payload)
    }

    const shallShowUserProfileSidebar = ref(false)
    const showUserProfileSidebar = () => {
      shallShowUserProfileSidebar.value = true
    }

    // Active Chat Contact Details
    const shallShowActiveChatContactSidebar = ref(false)

    // UI + SM Devices
    // Left Sidebar Responsiveness
    const { mqShallShowLeftSidebar } = useResponsiveAppLeftSidebarVisibility()
    watch(mqShallShowLeftSidebar, (value, oldValue) => {
      if (value && value !== oldValue) {
        activeChat.value = {}
      }
    })

    const startConversation = () => {
      if (store.state.app.windowWidth < $themeBreakpoints.lg) {
        mqShallShowLeftSidebar.value = true
      }
    }

    // Gift
    const openGifts = () => {
      showGifts.value = !showGifts.value
    }

    const showGiftModal = gift => {
      selectedGift.value = gift
      showGifts.value = false
      chooseGiftModal.value = false
      // giftModal.value = true

      gtmTrackNewEvent('giftclick', {
        category: 'Chat',
        action: 'Giftclick',
        label: activeChat.value.contact.id,
      })

      // eslint-disable-next-line no-use-before-define
      sendGift(gift)
    }

    const hideGiftModal = (showChooseAnotherGift = false) => {
      selectedGift.value = null
      giftModal.value = false
      showGifts.value = true

      if (showChooseAnotherGift) chooseGiftModal.value = true
    }

    // Hediye gönder
    const sendGift = gift => {
      if (!gift) return

      loading.value = true

      const payload = {
        from: profileUserDataMinimal.value.id,
        to: activeChat.value.contact.id,
        type: 'CHAT_GIFT',
        content: gift.productId,
      }

      // Firebase Analytics Event
      if (window.firebaseAnalytics) {
        logEvent(window.firebaseAnalytics, 'web_gift_send', {
          fromId: store.getters['auth/userId'],
          toId: activeChat.value.contact.id,
          giftType: gift.productId,
        })
      }

      sendMessage(payload, false, gift)
      hideGiftModal()
    }

    // Gift görselini getir
    // eslint-disable-next-line global-require,import/no-dynamic-require
    const getImgGift = name => require(`@/assets/images/gifts/${name}`)

    // Market sayfasına yönlendir
    const goMarket = () => {
      if (!canShowPremiumChatPopup()) return
      premiumChatModal.value = true
    }

    const openPremiumPackage = () => {
      premiumChatModal.value = false
      const appUrl = process.env.VUE_APP_URL || window.location.origin
      window.location.href = `${appUrl.replace(/\/$/, '')}/payment/products/com.arasana.app.25DK`
    }

    // Kullanıcı bilgilerini çek
    const getUser = userId => {
      loading.value = true

      useJwt.getUser(userId)
        .then(response => {
          const user = response.data.body
          const contact = chatsContacts.value.find(c => c.id === user.id)
          if (!contact) {
            chatsContacts.value.unshift({
              id: user.id,
              lastMessage: {
                dateSeen: null,
                dateSent: new Date().getTime(),
                fromId: user.id,
                toId: profileUserDataMinimal.value.id,
                message: '',
                type: 'CHAT_MSG',
              },
              profile: user.profile,
              unSeenMsgCount: 0,
              userName: user.userName,
            })

            openChatOfContact(user.id)
          } else {
            activeChat.value = {
              ...activeChat.value,
              extended: { ...user },
            }
          }
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => { loading.value = false })
    }

    const reportUserSuccessText = t('Reporting user successful')
    const blockUserSuccessText = t('User blocking successful')
    const unblockUserSuccessText = t('Unblocking user successful')
    const deleteChatSuccessText = t('Deleted successfully')
    const deleteChatConfirmText = t('Are you sure you want to delete?')
    const deleteChatConfirmTitle = t('Confirmation')
    const deleteChatOkTitle = t('Delete')
    const deleteChatCancelTitle = t('Cancel')

    const deleteChatHistory = id => {
      vm.$bvModal.msgBoxConfirm(deleteChatConfirmText, {
        title: deleteChatConfirmTitle,
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: deleteChatOkTitle,
        cancelTitle: deleteChatCancelTitle,
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      }).then(value => {
        if (!value) return

        loading.value = true
        useJwt.chatDeleteHistory(id)
          .then(() => {
            showNotify(toast, deleteChatSuccessText, 'success')
            activeChat.value = {}
            fetchChatAndContacts(true)
          })
          .catch(error => {
            throwDefaultError(toast, error)
          })
          .finally(() => { loading.value = false })
      })
    }

    const blockUser = id => {
      loading.value = true
      useJwt.blockUser(id)
        .then(() => {
          showNotify(toast, blockUserSuccessText, 'success')
          activeChat.value = {}
          fetchChatAndContacts(true)
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => { loading.value = false })
    }

    const unBlockUser = id => {
      loading.value = true
      useJwt.unBlockUser(id)
        .then(() => {
          showNotify(toast, unblockUserSuccessText, 'success')
          fetchChatAndContacts(true)
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => { loading.value = false })
    }

    const reportUser = () => {
      // reportForm.value.validate().then(success => {
      //   if (success) {
      //     loading.value = true
      //     const data = {
      //       reportee: store.getters['auth/userName'],
      //       reporteeEmail: 'email@email.com',
      //       reported: activeChat.value.contact.userName,
      //       reason: report.value.reason,
      //       description: report.value.description,
      //     }
      //     useJwt.reportUser(data)
      //       .then(() => {
      //         showNotify(toast, reportUserSuccessText, 'success')
      //         modalReportUser.value = false
      //         report.value = {
      //           reason: '',
      //           description: '',
      //         }
      //       })
      //       .catch(error => {
      //         throwDefaultError(toast, error)
      //       })
      //       .finally(() => {
      //         loading.value = false
      //       })
      //   }
      // })

      // TODO: yeni şikayet sistemi
      loading.value = true

      const data = {
        related_id: null,
        reason: report.value.reason === 'Other' ? report.value.description : report.value.reason,
        report_source: 'chat',
        reported_uuid: activeChat.value.contact.id,
      }

      useJwt.reportUser2(data)
        .then(() => {
          showNotify(toast, reportUserSuccessText, 'success')
          modalReportUser.value = false

          report.value = {
            reason: '',
            description: '',
          }
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
        .finally(() => {
          loading.value = false
        })
    }

    const call = (contact, order = 0) => {
      if (isAiContact(contact)) {
        vm.$root.$emit('ai-voice-call:start', activeChat.value?.extended || contact)
        return
      }

      useJwt.getUser(contact.id)
        .then(response => {
          const user = response.data.body

          store.dispatch('app/setModalCallType', 'audio')
          store.dispatch('app/setCallDetails', {
            callSource: 'chat_view',
            userOrder: order,
          })
          store.dispatch('app/setModalCall', false)
          setTimeout(() => {
            store.dispatch('app/setModalCallUser', user)
            store.dispatch('app/setModalCall', true)
          }, 33)
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })

      // if (contact.profile?.callType == 2) {
      //   // Kullanıcı app üzerinden arama kabul ettiği için agora üzerinden arama yap
      //   // store.dispatch('app/setModalDownloadMobileApp', true)
      //   store.dispatch('agora/setShowAgoraCall', true)
      //   store.dispatch('agora/setCallUser', contact)
      // } else {
      //   if (!store.getters['auth/userPhoneNumber']) {
      //     store.dispatch('app/setModalAddPhoneNumberShowDiamond', false)
      //     store.dispatch('app/setModalAddPhoneNumber', true)
      //     store.dispatch('app/setVerificationType', 'phone')
      //   } else {
      //     loading.value = true
      //     useJwt.callUser(contact.id)
      //         .then(response => {
      //           showNotify(toast, 'Calling successful', 'success')
      //         })
      //         .catch(error => {
      //           const { response } = error
      //           throwDefaultError(this.$toast, error)
      //           if (response && response.data?.header?.error?.errorCode == 215) {
      //             store.dispatch('app/setModalAddPhoneNumberShowDiamond', false)
      //             store.dispatch('app/setModalAddPhoneNumber', true)
      //             store.dispatch('app/setVerificationType', 'phone')
      //           }
      //         })
      //         .finally(() => { loading.value = false })
      //   }
      // }
    }

    const videoCall = (contact, order = 0) => {
      if (isAiContact(contact)) {
        vm.$root.$emit('ai-voice-call:start', activeChat.value?.extended || contact)
        return
      }

      useJwt.getUser(contact.id)
        .then(response => {
          const user = response.data.body
          store.dispatch('app/setModalCallType', 'video')
          store.dispatch('app/setCallDetails', {
            callSource: 'chat_view',
            userOrder: order,
          })
          store.dispatch('app/setModalCall', false)
          setTimeout(() => {
            store.dispatch('app/setModalCallUser', user)
            store.dispatch('app/setModalCall', true)
          }, 33)
        })
        .catch(error => {
          throwDefaultError(toast, error)
        })
    }

    return {
      allMessagesLoaded,
      blockUser,
      call,
      chooseGiftModal,
      deleteChatHistory,
      getGifts,
      getImgGift,
      gifts,
      giftModal,
      goMarket,
      hideGiftModal,
      iconChat3,
      iconDiamonds1,
      iconMoreHorizontal,
      iconPhone,
      iconVideoCall,
      loading,
      loadingMore,
      onChatLogScroll,
      refChatInput,
      onChatImageLoaded,
      loadMore,
      messageInputText,
      mediaRequestLoading,
      modalReportUser,
      isFreeCallUser,
      canStartGame,
      showMoreWaysPanel,
      guulLoading,
      guulGameOpen,
      guulMatchUrl,
      guulPaywallModal,
      guulRequiredCredits,
      guulCurrentCredits,
      guulInsufficientCredits,
      guulPremiumPlacement,
      startGuulMatch,
      joinGuulMatch,
      confirmGuulPaywall,
      openGuulPremiumPackage,
      closeGuulPaywall,
      closeGuulGame,
      moreWaysOpen,
      numberFormat,
      openPremiumPackage,
      openGifts,
      perfectScrollbarSettings,
      premiumChatModal,
      report,
      reportForm,
      reportUser,
      reportReasonTypes,
      requestAssistantMedia,
      sendGift,
      selectedGift,
      showGiftModal,
      showGifts,
      showAssistantCallPrompt,
      dismissAssistantCallPrompt,
      unBlockUser,
      userGender,
      videoCall,

      // Filters
      // formatDate,

      // useChat
      resolveAvatarBadgeVariant,

      // Chat & Contacts
      chatsContacts,
      communityContacts,

      // Mood
      moodModal,
      currentMood,
      selectedMoodId,
      moodOptions,
      saveMood,

      // 1v1 list filter
      chatSort,
      chatMoodId,
      setChatSortMostRecent,
      setChatMoodFilter,

      // Single Chat
      refChatLogPS,
      refChatLog,
      handleAiFeedback,
      activeChat,
      activeChatCallAction,
      chatInputMessage,
      openChatOfContact,
      scrollToBottomInChatLog,
      send,
      startActiveChatCall,
      onTyping,
      isContactTyping,

      // Profile User Minimal Data
      profileUserDataMinimal,

      // User Profile Sidebar
      profileUserData,
      shallShowUserProfileSidebar,
      showUserProfileSidebar,

      // Active Chat Contact Details
      shallShowActiveChatContactSidebar,

      // UI + SM Devices
      startConversation,
      mqShallShowLeftSidebar,

      // socket
      socket,
      socketConnecting,
      socketConnected,

      // Message count and time
      messageCount,
      messageTimeLeft,
      freeChat,
      messageTimeLeftForHuman,
      messageCountLoading,
    }
  },
}
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
@import "~@core/scss/base/pages/app-chat.scss";
@import "~@core/scss/base/pages/app-chat-list.scss";
// ── MODERN DARK CHAT THEME (#11172b + Dark Plum + Neon Pink Accents) ───────────
.chat-application {
  background: #11172b !important;

  .content-area-wrapper {
    background: #0f0b1a !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 20px !important;
    overflow: hidden !important;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45) !important;
  }

  // ── LEFT SIDEBAR ──────────────────────────────────────────────
  .sidebar-left {
    .sidebar-content {
      background: #130e24 !important;
      border-right: 1px solid rgba(255, 255, 255, 0.08) !important;
      color: #ffffff !important;
    }

    .chat-title {
      color: #ffffff !important;
      font-size: 20px !important;
      font-weight: 700 !important;
      padding: 18px 20px 14px !important;
    }

    // Search bar
    .chat-fixed-search {
      padding: 0 1.2rem !important;

      .input-group {
        border-radius: 999px !important;
        background: rgba(255, 255, 255, 0.05) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        overflow: hidden;
      }

      .input-group-text {
        background: transparent !important;
        border: none !important;
        color: rgba(255, 255, 255, 0.45) !important;
        padding-left: 14px !important;
      }

      input {
        background: transparent !important;
        border: none !important;
        color: #ffffff !important;
        font-size: 13px !important;

        &::placeholder {
          color: rgba(255, 255, 255, 0.4) !important;
        }

        &:focus {
          box-shadow: none !important;
        }
      }
    }

    // Tabs Switcher Pill
    .chat-tabs {
      background: rgba(255, 255, 255, 0.06) !important;
      border-radius: 999px !important;
      padding: 4px !important;
      border: 1px solid rgba(255, 255, 255, 0.08) !important;
      margin: 12px 1.2rem !important;

      .chat-tab {
        border-radius: 999px !important;
        padding: 8px 16px !important;
        color: rgba(255, 255, 255, 0.6) !important;
        font-size: 13px !important;
        font-weight: 500 !important;
        transition: all 0.2s ease !important;

        &:hover {
          color: #ffffff !important;
        }

        &.active {
          background: #ffffff !important;
          color: #110c22 !important;
          font-weight: 700 !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25) !important;

          &::after {
            display: none !important;
          }
        }
      }
    }

    // Filters
    .chat-filters {
      margin: 8px 1.2rem 4px !important;

      .chat-filter-btn {
        background: rgba(255, 255, 255, 0.05) !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
        color: rgba(255, 255, 255, 0.65) !important;
        border-radius: 999px !important;

        &:hover {
          background: rgba(255, 255, 255, 0.1) !important;
          color: #fff !important;
        }

        &.active {
          background: rgba(234, 3, 178, 0.15) !important;
          border-color: rgba(234, 3, 178, 0.4) !important;
          color: #ff66cc !important;

          svg {
            color: #ff66cc !important;
          }
        }
      }
    }

    // Contact List
    .chat-user-list-wrapper {
      li {
        margin: 4px 8px !important;
        border-radius: 14px !important;
        transition: all 0.2s ease !important;
        background: transparent !important;
        padding: 0.75rem 1rem !important;

        &:hover {
          background: rgba(255, 255, 255, 0.05) !important;
        }

        &.active {
          background: rgba(168, 85, 247, 0.16) !important;
          border-left: 3px solid #ea03b2 !important;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2) !important;
        }

        .chat-info {
          h5 {
            color: #ffffff !important;
            font-weight: 600 !important;
            font-size: 14px !important;
          }

          .card-text, p {
            color: rgba(255, 255, 255, 0.55) !important;
            font-size: 12px !important;
          }
        }

        .chat-time {
          color: rgba(255, 255, 255, 0.4) !important;
          font-size: 11px !important;
        }

        .badge-primary {
          background-color: #ea03b2 !important;
          color: #ffffff !important;
          font-weight: 700 !important;
          font-size: 10px !important;
          border-radius: 999px !important;
          padding: 0.28rem 0.55rem !important;
        }
      }
    }
  }

  // ── ACTIVE CHAT WINDOW ────────────────────────────────────────
  .chat-app-window {
    background: #0f0b1a !important;

    .start-chat-area {
      background: #0f0b1a !important;
      color: #ffffff !important;

      // Aktif sohbette mesaj yokken: arkaplan fotoğrafını kapatmasın, ortalı ve okunur olsun
      &.start-chat-area--empty {
        background: transparent !important;
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100%;
        padding: 24px 16px;
        text-align: center;

        .empty-chat-icon svg {
          width: 120px;
          height: 120px;
          filter: drop-shadow(0 10px 28px rgba(234, 3, 178, 0.35));
          animation: bohoEmptyChatFloat 3.2s ease-in-out infinite;
        }

        .empty-chat-text {
          color: rgba(255, 255, 255, 0.92) !important;
          background: transparent !important;
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
        }

        .empty-chat-subtext {
          margin: 6px 0 0;
          color: rgba(226, 220, 255, 0.7);
          font-size: 14px;
          max-width: 280px;
        }

        @keyframes bohoEmptyChatFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }

        @media (max-width: 575px) {
          .empty-chat-icon svg { width: 96px; height: 96px; }
          .empty-chat-text { font-size: 16px; }
          .empty-chat-subtext { font-size: 13px; }
        }
      }

      .start-chat-icon {
        background: #18112e !important;
        color: #a855f7 !important;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
      }

      .start-chat-text {
        background: #18112e !important;
        color: #ffffff !important;
        border: 1px solid rgba(255, 255, 255, 0.08) !important;
      }
    }

    .active-chat {
      display: flex;
      flex-direction: column;
      // Core'daki calc(100vh - ...) yüksekliği mobilde kutudan taşırıp gönderme alanını gizliyordu
      height: 100% !important;
      min-height: 0;
      position: relative;
      background: #0f0b1a !important;
      overflow: hidden;
    }

    // Ambient watermark background
    .chat-ambient-background {
      // Standart boyut: ekranı kaplamak yerine ortada, kenarları yumuşakça eriyen portre
      position: absolute;
      top: 50%;
      left: 50%;
      width: min(420px, 78%);
      aspect-ratio: 3 / 4;
      max-height: 82%;
      transform: translate(-50%, -50%);
      background-size: cover;
      background-position: center 25%;
      background-repeat: no-repeat;
      opacity: 0.16;
      filter: blur(1px) contrast(1.1);
      -webkit-mask-image: radial-gradient(ellipse at center, #000 45%, transparent 75%);
      mask-image: radial-gradient(ellipse at center, #000 45%, transparent 75%);
      pointer-events: none;
      z-index: 0;
    }

    // Chat Header
    .chat-navbar {
      flex: 0 0 auto;
      z-index: 2;

      .chat-header {
        background: #130e24 !important;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
        height: 64px !important;
        padding: 0 1.25rem !important;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .chat-user-username {
          color: #ffffff !important;
          font-size: 15px !important;
          font-weight: 700 !important;
          line-height: 1.2;
        }

        .chat-header-status-row {
          display: flex;
          align-items: center;
          gap: 5px;
          margin-top: 2px;

          .chat-online-dot {
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background-color: #10b981;
            box-shadow: 0 0 6px #10b981;

            &.offline {
              background-color: #ef4444;
              box-shadow: 0 0 6px #ef4444;
            }
          }

          .chat-online-text {
            font-size: 11px;
            color: #10b981;
            font-weight: 500;

            &.offline {
              color: #ef4444;
            }
          }
        }

        .chat-header-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.07);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(234, 3, 178, 0.2);
            border-color: #ea03b2;
            color: #ff66cc;
            transform: scale(1.05);
          }
        }
      }
    }

    // User chats conversation scroll
    // Mesaj altındaki saatler beyaz (gelen ve giden).
    // Gelenlerde core kuralı .chat-left .chat-body div { color:#000 !important } saati siyah yapıyordu.
    .chats .chat-body div.chat-footer,
    .chats .chat-body div.chat-footer span,
    .chats .chat-left .chat-body div.chat-footer,
    .chats .chat-left .chat-body div.chat-footer span {
      color: rgba(255, 255, 255, 0.8) !important;
    }

    .user-chats {
      flex: 1 1 auto;
      min-height: 0;
      height: auto !important;
      background: transparent !important;
      position: relative;
      z-index: 1;
      padding: 1rem 1.25rem !important;
      // Listenin ucuna gelince kaydırma sayfaya geçmesin
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }


    // Actions (Ask for photo / Ask for video) - Left-aligned & larger
    .chat-floating-actions {
      display: flex;
      align-items: center;
      justify-content: flex-start !important;
      gap: 14px;
      padding: 10px 1.25rem 8px !important;
      z-index: 3;
      background: transparent;

      .chat-floating-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 44px;
        padding: 10px 22px;
        background: rgba(22, 14, 38, 0.94);
        backdrop-filter: blur(10px);
        border: 1.5px solid rgba(234, 3, 178, 0.7);
        border-radius: 999px;
        color: #ff66cc;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4), 0 0 12px rgba(234, 3, 178, 0.25);

        &:hover:not(:disabled) {
          background: rgba(234, 3, 178, 0.25);
          border-color: #ea03b2;
          color: #ffffff;
          box-shadow: 0 8px 24px rgba(234, 3, 178, 0.5);
          transform: translateY(-2px);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    // Chat Footer & Input
    .chat-footer-area {
      flex: 0 0 auto;
      background: #120d22 !important;
      border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
      padding: 10px 1.25rem 14px !important;
      z-index: 3;
    }

    .chat-app-form {
      position: static !important;
      bottom: auto !important;
      width: 100% !important;
      padding: 0 !important;
      background: transparent !important;
    }

    .chat-modern-input-bar {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
    }

    .chat-modern-input-field {
      flex: 1;
      height: 42px;
      border-radius: 999px !important;
      background: rgba(255, 255, 255, 0.07) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
      color: #ffffff !important;
      padding: 0 20px !important;
      font-size: 14px;
      transition: all 0.2s ease;

      &::placeholder {
        color: rgba(255, 255, 255, 0.35) !important;
      }

      &:focus {
        background: rgba(255, 255, 255, 0.1) !important;
        border-color: rgba(234, 3, 178, 0.6) !important;
        box-shadow: 0 0 12px rgba(234, 3, 178, 0.25) !important;
      }
    }

    .chat-modern-send-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: linear-gradient(135deg, #7928ca 0%, #ea03b2 100%);
      border: none;
      color: #ffffff;
      cursor: pointer;
      flex-shrink: 0;
      box-shadow: 0 4px 14px rgba(234, 3, 178, 0.4);
      transition: all 0.2s ease;

      &:hover:not(:disabled) {
        transform: scale(1.08);
        box-shadow: 0 6px 18px rgba(234, 3, 178, 0.6);
      }

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }
  }
}

.chat-assistant-tools {
  flex: 0 0 auto;
  padding: 0 1rem 0.65rem;
}

.assistant-call-prompt {
  position: relative;
  margin-bottom: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(255, 116, 82, 0.18);
  border-radius: 20px;
  background: #fff4ea;

  &__close {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 50%;
    color: #6f6f78;
    background: rgba(0, 0, 0, 0.08);
  }

  h3 {
    margin: 0 2rem 0.25rem 0;
    color: #29292f;
    font-size: 20px;
    line-height: 1.15;
    font-weight: 800;
  }

  p {
    max-width: 520px;
    margin: 0 2rem 0.9rem 0;
    color: #76727a;
    font-size: 15px;
    line-height: 1.3;
  }

  &__cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: 0;
    border-radius: 16px;
    padding: 0.8rem 1rem;
    color: #fff;
    background: linear-gradient(100deg, #ff5a32 0%, #ff8068 100%);
    font-size: 17px;
    font-weight: 800;
  }

  &__secure {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin-top: 0.75rem;
    color: #76727a;
    font-size: 14px;
  }
}

.assistant-more-ways {
  border-radius: 20px;
  background: rgba(124, 86, 255, 0.08);

  &--open {
    padding: 0.75rem;
    background: #fff;
    box-shadow: 0 8px 22px rgba(28, 28, 28, 0.06);
  }

  &__toggle {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 24px;
    align-items: center;
    gap: 0.55rem;
    width: 100%;
    border: 0;
    border-radius: 18px;
    padding: 0.85rem 1rem;
    color: #7953ff;
    background: transparent;
    text-align: left;

    strong {
      color: inherit;
      font-size: 18px;
      line-height: 1.2;
      font-weight: 800;
    }

    svg:last-child {
      justify-self: end;
    }
  }

  &--open &__toggle {
    color: #29292f;
    padding: 0.35rem 0.25rem 0.85rem;
  }

  &__spark {
    display: inline-flex;
    color: #7953ff;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 118px;
    border: 1px solid #ececf1;
    border-radius: 16px;
    padding: 0.8rem 0.45rem;
    color: #29292f;
    background: #fff;

    &:disabled {
      opacity: 0.55;
    }

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 58px;
      height: 58px;
      margin-bottom: 0.65rem;
      border-radius: 18px;
      color: #7953ff;
      background: rgba(121, 83, 255, 0.12);
    }

    strong {
      font-size: 15px;
      line-height: 1.2;
      font-weight: 800;
      text-align: center;
    }
  }
}

@media screen and (max-width: 575px) {
  .chat-assistant-tools {
    padding: 0 0.85rem 0.7rem;
  }

  .assistant-more-ways__grid {
    gap: 0.55rem;
  }

  .assistant-more-ways__item {
    min-height: 104px;

    span {
      width: 52px;
      height: 52px;
    }

    strong {
      font-size: 14px;
    }
  }
}
.message-information {
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-top: 0 !important;
  margin-bottom: 12px !important;
  padding: 12px 16px;
  color: #fff;
  background: linear-gradient(135deg, rgba(43, 26, 99, 0.92) 0%, rgba(29, 18, 71, 0.92) 55%, rgba(58, 19, 86, 0.92) 100%);
  border: 1px solid rgba(150, 110, 255, 0.28);
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(6, 2, 24, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05);

  /* sağ üstte hafif pembe parıltı */
  &::before {
    content: '';
    position: absolute;
    top: -60%;
    right: -5%;
    width: 45%;
    height: 220%;
    background: radial-gradient(ellipse at center, rgba(234, 3, 178, 0.28) 0%, transparent 65%);
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  .remaining-message {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-width: 0;

    .chat-icon {
      width: 42px;
      height: 42px;
      flex-shrink: 0;
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #8b5cf6, #a855f7);
      box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35);

      img {
        width: 24px;
        height: 24px;
        filter: brightness(0) invert(1);
      }
    }

    .remaining-message-text {
      display: flex;
      justify-content: center;
      flex-direction: column;
      margin-left: 12px;
      min-width: 0;

      .remaining-message-text-title {
        display: block;
        @media screen and (max-width: 767px) {
          display: none;
        }
      }

      p {
        color: rgba(236, 232, 255, 0.88);
        font-size: 13px;
        font-weight: 500;
        line-height: 18px;
        margin-bottom: 0;

        .text-white {
          color: #fff !important;
          font-weight: 700;
        }
      }
    }
  }

  .market-button {
    flex-shrink: 0;

    a {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 8px 18px;
      border-radius: 9999px;
      color: #fff;
      font-size: 13px;
      font-weight: 700;
      text-decoration: none;
      /* menübardaki seçili sekmeyle aynı gradyan */
      background: linear-gradient(135deg, rgba(234, 3, 178, 0.28) 0%, rgba(168, 85, 247, 0.22) 50%, rgba(99, 102, 241, 0.12) 100%);
      border: 1px solid rgba(234, 3, 178, 0.35);
      box-shadow: 0 2px 14px rgba(234, 3, 178, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.25), inset 0 0 12px rgba(234, 3, 178, 0.12);
      transition: filter .15s ease, transform .15s ease;

      &:hover {
        color: #fff;
        filter: brightness(1.2);
        transform: translateY(-1px);
      }
    }

    img {
      margin-right: 0;
      filter: drop-shadow(0 0 4px rgba(234, 3, 178, 0.55));
    }

    @media screen and (max-width: 767px) {
      a {
        padding: 8px 12px;
        font-size: 12px;
      }
    }
  }

  @media screen and (max-width: 575px) {
    padding: 10px 12px;

    .remaining-message .chat-icon {
      width: 36px;
      height: 36px;
    }

    .remaining-message .remaining-message-text p {
      font-size: 12px;
      line-height: 16px;
    }
  }
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.chat-container {
  overflow-y: auto;
  height: 100%;
}

.premium-chat-modal {
  .modal-dialog {
    max-width: 360px;
  }

  .premium-chat-modal-content {
    border: 0;
    border-radius: 24px;
    overflow: hidden;
    background: #fff8f1;
    box-shadow: 0 28px 70px rgba(20, 20, 20, 0.35);
  }
}

.premium-chat-paywall {
  position: relative;
  padding: 32px 22px 24px;
  text-align: center;
  color: #28282f;

  &__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: #6f6f78;
    padding: 0;
  }

  &__hero {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 52px;
    margin-bottom: 18px;
    border-radius: 16px;
    color: #fff;
    background: linear-gradient(135deg, #744bff 0%, #8b5cff 100%);
    transform: rotate(-7deg);
    box-shadow: 0 18px 38px rgba(116, 75, 255, 0.28);

    span {
      position: absolute;
      right: -12px;
      bottom: -8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: #ff405d;
      border: 4px solid #fff8f1;
      font-weight: 800;
      transform: rotate(7deg);
    }
  }

  h2 {
    max-width: 290px;
    margin: 0 auto 12px;
    font-size: 24px;
    line-height: 1.16;
    font-weight: 800;
  }

  p {
    max-width: 300px;
    margin: 0 auto 18px;
    color: #76727a;
    font-size: 15px;
    line-height: 1.28;
  }

  &__benefit {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 14px 16px;
    border-radius: 18px;
    background: linear-gradient(90deg, rgba(121, 83, 255, 0.12), rgba(255, 116, 139, 0.10));
    text-align: left;

    strong,
    span {
      display: block;
    }

    strong {
      font-size: 17px;
      line-height: 1.15;
      font-weight: 800;
    }

    span {
      color: #7953ff;
      font-size: 15px;
      font-weight: 700;
    }
  }

  &__benefit-icon,
  &__cta-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 15px;
    color: #fff;
    background: linear-gradient(135deg, #744bff, #8c5cff);
  }

  &__spark {
    margin-left: auto;
    color: #7953ff;
  }

  &__cta {
    align-items: center;
    display: grid;
    grid-template-columns: 46px minmax(0, 1fr) 22px;
    gap: 12px;
    width: 100%;
    border: 0;
    border-radius: 18px;
    padding: 14px 16px;
    color: #fff;
    text-align: left;
    background: linear-gradient(100deg, #764dff 0%, #d83bdb 48%, #ff7f66 100%);
    box-shadow: 0 16px 30px rgba(216, 59, 219, 0.26);

    strong,
    small {
      display: block;
    }

    strong {
      font-size: 17px;
      line-height: 1.15;
      font-weight: 800;
    }

    small {
      color: rgba(255, 255, 255, 0.86);
      font-size: 14px;
      line-height: 1.25;
    }

    svg:last-child {
      justify-self: end;
    }
  }
}

@media screen and (max-width: 575px) {
  .premium-chat-modal {
    .modal-dialog {
      margin: 0 18px;
    }
  }

  .premium-chat-paywall {
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    padding: 28px 18px 22px;

    h2 {
      font-size: 22px;
    }

    p {
      font-size: 14px;
    }

    &__cta {
      grid-template-columns: 42px minmax(0, 1fr) 20px;
    }
  }
}
.guul-paywall-modal {
  .modal-dialog {
    max-width: 390px;
  }

  .guul-paywall-modal-content {
    border: 0;
    border-radius: 24px;
    overflow: hidden;
    background: #fff8f1;
    box-shadow: 0 28px 70px rgba(20, 20, 20, 0.35);
  }
}

.guul-paywall {
  position: relative;
  padding: 32px 22px 24px;
  text-align: center;
  color: #28282f;

  &__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: #6f6f78;
    padding: 0;
  }

  &__hero {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 52px;
    margin-bottom: 18px;
    border-radius: 16px;
    color: #fff;
    background: linear-gradient(135deg, #744bff 0%, #8b5cff 100%);
    transform: rotate(-7deg);
    box-shadow: 0 18px 38px rgba(116, 75, 255, 0.28);

    span {
      position: absolute;
      right: -12px;
      bottom: -8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      color: #fff;
      background: #ff6848;
      border: 4px solid #fff8f1;
      transform: rotate(7deg);
    }
  }

  h2 {
    max-width: 320px;
    margin: 0 auto 12px;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 800;
  }

  p {
    max-width: 320px;
    margin: 0 auto 18px;
    color: #76727a;
    font-size: 15px;
    line-height: 1.3;
  }

  &__row,
  &__balance {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    padding: 14px 16px;
    border-radius: 18px;
    background: rgba(255, 103, 72, 0.08);
    text-align: left;
  }

  &__media-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    color: #fff;
    background: #ff6848;
  }

  &__row-main {
    // min-width: 0 olmadan flex öğesi içeriğinin altına inemez;
    // uzun çevirilerde metin kelime kelime kırılıyordu
    flex: 1 1 auto;
    min-width: 0;

    span,
    strong,
    em {
      display: block;
    }

    span {
      color: #76727a;
      font-size: 13px;
      line-height: 1.2;
    }

    strong {
      color: #ff6848;
      font-size: 18px;
      line-height: 1.15;
      font-weight: 800;
    }
  }

  // Oyun adı + adet fiyatın altında. Sağ sütunda yan yana durunca
  // uzun dillerde (tr, de, ru) satır sığmıyor ve sol tarafı eziyordu.
  &__row-meta {
    margin-top: 6px;
    color: #76727a;
    font-size: 13px;
    line-height: 1.25;
    font-style: normal;
  }

  &__balance {
    background: rgba(255, 103, 72, 0.07);

    span,
    strong {
      display: block;
    }

    span {
      color: #76727a;
      font-size: 15px;
    }

    strong {
      color: #28282f;
      font-size: 26px;
      line-height: 1.1;
      font-weight: 800;
    }
  }

  &__cta {
    align-items: center;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 22px;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
    border: 0;
    border-radius: 18px;
    padding: 14px 16px;
    color: #fff;
    text-align: left;
    background: linear-gradient(100deg, #ff5a32 0%, #ff7b68 100%);
    box-shadow: 0 16px 30px rgba(255, 90, 50, 0.24);

    &:disabled {
      opacity: 0.75;
    }

    span {
      min-width: 0;
    }

    strong,
    small,
    em {
      display: block;
      font-style: normal;
    }

    strong {
      font-size: 16px;
      line-height: 1.15;
      font-weight: 800;
    }

    small {
      color: #fff;
      font-size: 14px;
      line-height: 1.25;
      font-weight: 800;
    }

    em {
      color: rgba(255, 255, 255, 0.86);
      font-size: 13px;
      line-height: 1.25;
    }
  }

  &__secondary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    margin-top: 12px;
    padding: 12px 16px;
    border: 1px solid rgba(255, 103, 72, 0.35);
    border-radius: 18px;
    background: transparent;
    color: #ff6848;
    font-size: 15px;
    font-weight: 700;

    &:disabled {
      opacity: 0.6;
    }
  }

  &__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 16px;
    color: #77727a;
    font-size: 13px;
    line-height: 1.3;
    text-align: left;
  }
}

@media screen and (max-width: 575px) {
  .guul-paywall-modal {
    .modal-dialog {
      margin: 0 18px;
    }
  }

  .guul-paywall {
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    padding: 28px 18px 22px;

    h2 {
      font-size: 21px;
    }

    p {
      font-size: 14px;
    }

    &__cta {
      grid-template-columns: 42px minmax(0, 1fr) 20px;
    }
  }
}
// Oyun katmanı.
// iframe'in üstünde kaydırılabilir (overflow: auto/scroll) ya da transform'lu bir ata
// eleman İSTEMİYORUZ: iOS Safari böyle bir atanın altındaki iframe'in dokunma
// koordinatlarını kaydırıyor. Ortalama flex ile yapılıyor, transform kullanılmıyor.
// 100vh de kullanılmıyor; top/bottom: 0 iOS'taki adres çubuğu sorununu çözüyor.
.guul-game-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1061;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #101014;

  &__window {
    position: relative;
    width: 100%;
    height: 100%;
    background: #101014;
  }

  &__frame {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    color: #fff;
    background: rgba(0, 0, 0, 0.55);
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.75);
    }
  }
}

// Masaüstünde tam ekran yerine ortalanmış popup.
// Oyun dikey (telefon) düzende tasarlandığı için pencere de dar ve uzun tutuluyor.
@media (min-width: 768px) {
  .guul-game-overlay {
    background: rgba(16, 16, 20, 0.72);
    padding: 24px;

    &__window {
      width: min(440px, 100%);
      height: min(780px, 100%);
      border-radius: 20px;
      // Yalnızca köşeleri kırpmak için; kaydırma yaratmıyor
      overflow: hidden;
      box-shadow: 0 28px 70px rgba(0, 0, 0, 0.45);
    }

    &__close {
      top: 10px;
      right: 10px;
    }
  }
}

body.guul-game-open {
  overflow: hidden;
}
</style>

<style lang="scss">
/* Sohbet üst menüsü (3 nokta) ve şikayet penceresi: koyu Boho teması.
   Önceden açık zemin üstünde beyaz yazı kalıyordu ve içerik okunmuyordu. */
.dropdown-menu.chat-header-menu {
  min-width: 210px;
  padding: 6px !important;
  background: linear-gradient(180deg, #1f1035 0%, #150a28 100%) !important;
  border: 1px solid rgba(168, 85, 247, 0.35) !important;
  border-radius: 14px !important;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55) !important;

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px !important;
    border-radius: 10px;
    color: #f4f1ff !important;
    font-weight: 600;
    background: transparent !important;

    &:hover,
    &:focus {
      background: rgba(139, 92, 246, 0.28) !important;
      color: #fff !important;
    }
  }

  .chat-menu-icon {
    flex-shrink: 0;
    color: #c4a8ff;
  }

  .dropdown-item.chat-menu-report,
  .dropdown-item.chat-menu-report .chat-menu-icon {
    color: #fbbf24 !important;
  }

  .dropdown-item.chat-menu-danger,
  .dropdown-item.chat-menu-danger .chat-menu-icon {
    color: #f87171 !important;
  }
}

.chat-report-modal {
  .modal-content {
    background: linear-gradient(180deg, #1f1035 0%, #120822 100%) !important;
    border: 1px solid rgba(168, 85, 247, 0.35) !important;
    border-radius: 20px !important;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.8) !important;
    color: #f4f1ff;
  }

  .modal-header {
    border-bottom: 1px solid rgba(168, 85, 247, 0.2) !important;
    background: transparent !important;
  }

  .modal-title {
    color: #fff !important;
    font-weight: 800;
  }

  label,
  .col-form-label {
    color: rgba(236, 232, 255, 0.75) !important;
  }

  .vs__dropdown-toggle,
  textarea.form-control {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(168, 85, 247, 0.35) !important;
    border-radius: 12px !important;
    color: #fff !important;
  }

  .vs__selected,
  .vs__search,
  .vs__search::placeholder,
  textarea.form-control::placeholder {
    color: #fff !important;
  }

  textarea.form-control::placeholder {
    color: rgba(236, 232, 255, 0.5) !important;
  }

  .vs__open-indicator,
  .vs__clear {
    fill: #c4a8ff !important;
  }

  .vs__dropdown-menu {
    background: #1e1440 !important;
    border: 1px solid rgba(168, 85, 247, 0.35) !important;
    border-radius: 12px !important;

    .vs__dropdown-option {
      color: #f4f1ff !important;
    }

    .vs__dropdown-option--highlight {
      background: rgba(139, 92, 246, 0.35) !important;
      color: #fff !important;
    }
  }

  .chat-report-submit {
    width: 100%;
    height: 46px;
    border: 0 !important;
    border-radius: 999px !important;
    font-weight: 700;
    background: linear-gradient(135deg, #7c3aed, #ea03b2) !important;
    box-shadow: 0 8px 20px rgba(234, 3, 178, 0.3) !important;
  }
}

// Mobil klavye açıkken (body.chat-kb-open, bkz. onVisualViewportChange): sohbet görünen alana sığar,
// alt menü ve üstteki bilgi bandı / yüzen butonlar mesajlara yer açmak için gizlenir.
// Klavye kapanınca bu kurallar kalkar, görünüm aynen eski haline döner.
body.chat-kb-open {
  overscroll-behavior: none;

  // Görünen alana sabitlenir: iOS'un klavye kaydırmasını (offsetTop) takip eder. Üst menü bu sırada
  // görünen alanın dışında kalırsa onun için ayrılan boşluk da kalkar.
  .app-content.content.chat-application {
    position: fixed !important;
    top: var(--chat-vv-top, 0px) !important;
    left: 0;
    right: 0;
    margin-top: 0 !important;
    height: var(--chat-vh) !important;
    padding-top: max(8px, calc(72px - var(--chat-vv-top, 0px))) !important;
    padding-bottom: 8px !important;
  }

  .tab-bar,
  .message-information,
  .chat-floating-actions {
    display: none !important;
  }
}

// Dokunmatik ekranda butona dokunduktan sonra hover büyümesi takılı kalmasın
@media (hover: none) {
  .chat-application .chat-header-icon-btn:hover,
  .chat-application .chat-floating-btn:hover,
  .chat-application .chat-modern-send-btn:hover {
    transform: none !important;
  }
}

// Sohbet listesi: liste alanı başlık/arama/sekmelerden sonra kalan yüksekliği alır. Önceden yüksekliği
// kartın tamamına göre hesaplanıyordu; başlık kısmı kadar kartın dışına taşıyor, son sohbetler kesik
// kalıyor ve kaydırarak bile tam görünmüyordu.
.chat-application .sidebar-left .sidebar-content {
  display: flex;
  flex-direction: column;

  .chat-user-list-wrapper {
    flex: 1 1 auto;
    min-height: 0;
    height: auto !important;
  }
}

// Mobil sohbet listesi (Nirva gibi): kart ekranın en altına kadar uzanır, alt menü listenin üstünde
// durur ve liste onun arkasından akar; son sohbet alt menünün üstüne kadar kaydırılabilir.
@media (max-width: 768px) {
  body:not(.chat-conversation-open) {
    .vertical-layout .app-content.content.chat-application {
      padding-bottom: 0 !important;
    }

    .vertical-layout .app-content.content.chat-application .content-area-wrapper {
      border-bottom: 0 !important;
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      box-shadow: none !important;
    }

    .chat-application .sidebar-content .chat-user-list-wrapper {
      padding-bottom: calc(62px + 12px + env(safe-area-inset-bottom, 0px)) !important;
    }
  }
}

// Mobil: açık sohbet, üst menüyü ve alt menüyü örten tam ekran bir katmandır (kendi başlığı ve geri
// oku var); her zaman görünen alana (visualViewport) oturur, klavye açılınca mesaj kutusu klavyenin
// hemen üstünde kalır. Sohbet listesine dönünce menüler geri gelir. Renk ve bileşenler aynı kalır.
@media (max-width: 768px) {
  body.chat-conversation-open {
    .tab-bar {
      display: none !important;
    }

    .app-content.content.chat-application {
      position: fixed !important;
      top: var(--chat-vv-top, 0px) !important;
      left: 0;
      right: 0;
      z-index: 1032;
      height: 100vh !important;
      height: var(--chat-vh, 100dvh) !important;
      margin: 0 !important;
      padding: 0 !important;
      // Katman opak olmalı: şeffaf kalınca arkadaki üst menünün simgeleri yarı saydam bilgi bandının
      // içinden görünüyordu
      background: #11172b !important;
    }

    .chat-application .content-area-wrapper {
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    .chat-application .chat-footer-area {
      padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px)) !important;
    }

    // 16px: iOS yazı alanına dokununca sayfayı yakınlaştırmasın; 46px: rahat dokunma alanı
    .chat-application .chat-modern-input-field {
      height: 46px;
      font-size: 16px;
    }

    .chat-application .chat-modern-send-btn {
      width: 46px;
      height: 46px;
    }
  }
}

// Sohbet sayfası ekrana tam oturur ve sayfanın kendisi kaydırılamaz (yalnızca listeler kayar).
// Önceden kabın üstünde 12px boşluk (margin) vardı; tam ekran yüksekliğiyle birlikte sayfa 12px uzun
// oluyor, iPhone'da sayfa kayıp üst bant menünün altına giriyor, altta boşluk açılıyordu.
html.chat-page,
html.chat-page body {
  overflow: hidden;
  overscroll-behavior: none;
}

.vertical-layout .app-content.content.chat-application {
  margin-top: 0 !important;
}
</style>
